import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import './Orders.css';
import './DineInPage.css';
import { onMessage } from 'firebase/messaging';
import { messaging } from '../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import WhatsAppChatModal from './WhatsAppChatModal';
import QueueService from './QueueService';
import MediaUpload from './MediaUpload';
import OutletBadge from './OutletBadge';

const apiUrl = process.env.REACT_APP_API_URL;
const BASE_URL = `${apiUrl}/business`;
const CATALOG_EXPORT_URL = 'http://localhost:9001/business/catalog/menu/export/petpooja?business_id=2';
const CREATE_ORDER_URL = 'http://localhost:8000/orders';

const CHECKOUT_DELIVERY_SLOTS = [
  { value: '11:00-15:00', label: '11 AM to 3 PM' },
  { value: '18:00-23:00', label: '6 PM to 11 PM' },
];

// Helper: Convert to IST
const convertToIST = (dateString) => {
  if (!dateString) return null;
  try {
    const utcDate = new Date(dateString);
    return new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
  } catch (e) { return null; }
};

const formatCurrency = (amount, currency) => {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', minimumFractionDigits: 0 }).format(amount);
  } catch (e) { return `${amount} ${currency || 'INR'}`; }
};

const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  try {
    const istDate = convertToIST(dateString);
    if (!istDate) return dateString;
    const opts = { day: 'numeric', month: 'short', year: 'numeric' };
    if (includeTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
    return istDate.toLocaleString('en-GB', opts);
  } catch (e) { return dateString; }
};

const formatDateNoYear = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  try {
    const istDate = convertToIST(dateString);
    if (!istDate) return dateString;
    const opts = { day: 'numeric', month: 'short' };
    if (includeTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
    return istDate.toLocaleString('en-GB', opts);
  } catch (e) { return dateString; }
};

const getLatLon = (address) => {
  if (!address) return null;
  const lat = address.lat ?? address.latitude;
  const lon = address.lon ?? address.longitude;
  if (lat !== undefined && lon !== undefined && lat !== null && lon !== null) {
    return { lat, lon };
  }
  return null;
};

const formatSlotTime = (start, end) => {
  if (!start || !end) return '';
  try {
    if (typeof start === 'string' && !start.includes('T') && !start.includes('-') && start.split(':').length >= 2) {
      const formatStringTime = (tStr) => {
        const parts = tStr.split(':');
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayH = h % 12 || 12;
        const displayM = String(m).padStart(2, '0');
        return `${displayH}:${displayM} ${ampm}`;
      };
      return `${formatStringTime(start)} - ${formatStringTime(end)}`;
    }
    const sDate = new Date(start);
    const eDate = new Date(end);
    if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) {
      return `${start} - ${end}`;
    }
    const formatTime = (d) => {
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };
    return `${formatTime(sDate)} - ${formatTime(eDate)}`;
  } catch (e) {
    return `${start} - ${end}`;
  }
};

function isActive(val) {
  return String(val) === '1';
}

const rupee = (val) => `₹${parseFloat(val || 0).toFixed(0)}`;

const makeCartKey = (item, variation = null) => (
  variation ? `${item.itemid}_var_${variation.id}` : `${item.itemid}_simple`
);

const buildOrderReference = () => `ORD-${Date.now()}`;

const getNextNDays = (n = 7) => {
  const dates = [];
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today.getTime());
    d.setDate(today.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const value = `${y}-${m}-${da}`;

    let label = '';
    if (i === 0) label = 'Today';
    else if (i === 1) label = 'Tomorrow';
    else label = `${daysOfWeek[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;

    dates.push({ value, label });
  }
  return dates;
};

const getAvailableSlots = (biz) => {
  const metadata = biz?.business_metadata;
  if (!metadata) return [];
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return [];
  const methods = schedulingConfig.methods || {};
  const localDelivery = methods.local_delivery || {};
  if (!localDelivery.is_enabled) return [];
  const providers = localDelivery.providers || [];
  const slots = [];
  providers.forEach((prov) => {
    if (prov.slots) {
      prov.slots.forEach((s) => {
        slots.push({
          id: s.id,
          label: s.label,
          start: s.start,
          end: s.end,
          days_of_week: s.days_of_week || [],
          value: `${s.start}-${s.end}`
        });
      });
    }
  });
  return slots;
};

const isSlotActiveForDate = (slot, dateStr) => {
  if (!slot || !dateStr) return true;
  if (!slot.days_of_week || slot.days_of_week.length === 0) return true;

  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return true;
    const jsDay = d.getDay(); // 0 is Sunday, 1 is Monday, etc.
    const weekdayIndex = jsDay === 0 ? 6 : jsDay - 1; // 0 is Monday, 6 is Sunday
    return slot.days_of_week.includes(weekdayIndex);
  } catch (e) {
    return true;
  }
};

const normalizeOrderType = (typeVal) => {
  if (!typeVal) return 'HOME_DELIVERY';
  const u = String(typeVal).trim().toUpperCase();
  if (u === 'P' || u === 'PARCEL' || u === 'PICKUP') return 'PICKUP';
  if (u === 'D' || u === 'DINE_IN' || u === 'DINEIN') return 'DINE_IN';
  if (u === 'H' || u === 'HOME_DELIVERY' || u === 'DELIVERY') return 'HOME_DELIVERY';
  return u;
};

const calculateDeliveryPrice = (biz, pincodeStr, orderType = 'HOME_DELIVERY') => {
  if (normalizeOrderType(orderType) === 'PICKUP') return 0;
  if (!pincodeStr) return 40; // Fallback before pincode is entered
  const metadata = biz?.business_metadata;
  if (!metadata) return 40; // Default fallback
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return 40;

  const localDelivery = schedulingConfig.methods?.local_delivery;
  if (!localDelivery || !localDelivery.is_enabled) return 0;

  const provider = localDelivery.providers?.[0];
  if (!provider) return 0;

  const handlingFee = provider.handling_fee ?? 0;
  let shippingFee = 0;
  if (pincodeStr && provider.pincode_groups) {
    for (const group of provider.pincode_groups) {
      if (group.pincodes && group.pincodes.includes(pincodeStr.trim())) {
        shippingFee = group.shipping_fee ?? 0;
        break;
      }
    }
  }
  return handlingFee + shippingFee;
};

const getSupportedProviders = (biz, pincodeStr) => {
  const metadata = biz?.business_metadata;
  if (!metadata) return [];
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return [];
  const localDelivery = schedulingConfig.methods?.local_delivery;
  if (!localDelivery || !localDelivery.is_enabled) return [];
  const providers = localDelivery.providers || [];

  if (!pincodeStr || !pincodeStr.trim()) {
    return [];
  }
  const trimmedPin = pincodeStr.trim();
  return providers.filter(provider => {
    if (!provider.pincode_groups || provider.pincode_groups.length === 0) {
      return true;
    }
    return provider.pincode_groups.some(group =>
      group.pincodes && group.pincodes.includes(trimmedPin)
    );
  });
};

const calculateProviderDeliveryPrice = (provider, pincodeStr) => {
  if (!provider) return 0;
  const handlingFee = provider.handling_fee ?? 0;
  let shippingFee = 0;
  if (pincodeStr && provider.pincode_groups) {
    const trimmedPin = pincodeStr.trim();
    for (const group of provider.pincode_groups) {
      if (group.pincodes && group.pincodes.includes(trimmedPin)) {
        shippingFee = group.shipping_fee ?? 0;
        break;
      }
    }
  }
  return handlingFee + shippingFee;
};

const getProviderSlots = (provider) => {
  if (!provider || !provider.slots) return [];
  return provider.slots.map(s => ({
    id: s.id,
    label: s.label,
    start: s.start,
    end: s.end,
    days_of_week: s.days_of_week || [],
    value: `${s.start}-${s.end}`
  }));
};

const getPickupSlots = (biz, pickupLocationId) => {
  const metadata = biz?.business_metadata;
  if (!metadata) return [];
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return [];
  const pickup = schedulingConfig.methods?.pickup;
  if (!pickup || !pickup.is_enabled) return [];
  if (!pickupLocationId) return [];
  const loc = pickup.locations?.find(l => l.id === pickupLocationId);
  if (!loc || !loc.slots) return [];
  return loc.slots.map(s => ({
    id: s.id,
    label: s.label,
    start: s.start,
    end: s.end,
    days_of_week: s.days_of_week || [],
    value: `${s.start}-${s.end}`
  }));
};

const parseMenuResponse = (rawData) => {
  let data = rawData;
  if (rawData?.success === true && rawData?.data) {
    data = typeof rawData.data === 'string' ? JSON.parse(rawData.data) : rawData.data;
    if (data.response && typeof data.response === 'string') data = JSON.parse(data.response);
    else if (data.response && typeof data.response === 'object') data = data.response;
  }
  return data;
};

const normalizePetpoojaMenu = (data) => {
  const categories = (data?.categories || []).map((cat) => ({
    ...cat,
    categoryid: String(cat.categoryid ?? cat.id ?? ''),
    categoryname: cat.categoryname ?? cat.name ?? 'Category',
  }));

  const items = (data?.items || []).map((item) => ({
    ...item,
    itemid: String(item.itemid ?? item.id ?? ''),
    itemname: item.itemname ?? item.name ?? item.title ?? 'Item',
    itemdescription: item.itemdescription ?? item.description ?? '',
    item_categoryid: String(item.item_categoryid ?? item.categoryid ?? item.category_id ?? ''),
    item_image_url: item.item_image_url ?? item.image_url ?? item.image ?? '',
    price: item.price ?? item.item_price ?? 0,
    active: item.active ?? '1',
    itemallowvariation: item.itemallowvariation ?? (Array.isArray(item.variation) && item.variation.length > 0 ? '1' : '0'),
    variation: (item.variation || []).map((v) => ({
      ...v,
      id: String(v.id ?? v.variationid ?? ''),
      name: v.name ?? v.variationname ?? 'Variant',
      price: v.price ?? 0,
      active: v.active ?? '1',
      addon: v.addon || [],
    })),
    addon: item.addon || [],
  }));

  return { ...data, categories, items, addongroups: data?.addongroups || [] };
};

const getLocalDateString = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDeliveryDate = (order) => {
  if (order.delivery_date) return order.delivery_date.split('T')[0];
  return order.order_datetime ? order.order_datetime.split('T')[0] : null;
};

const getOrderDate = (order) => {
  return order.order_datetime ? order.order_datetime.split('T')[0] : null;
};

const getPresetRange = (preset, baseDate) => {
  if (!preset || preset === 'Custom' || preset === 'All') return { start: '', end: '' };
  const date = new Date(baseDate.getTime());
  date.setHours(0, 0, 0, 0);
  const fmt = getLocalDateString;

  if (preset === 'Active Orders') {
    const start = new Date(date); start.setDate(start.getDate() - 7);
    const end = new Date(date); end.setDate(end.getDate() + 30);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === 'Today') return { start: fmt(date), end: fmt(date) };
  if (preset === 'Tomorrow') {
    const tmrw = new Date(date); tmrw.setDate(tmrw.getDate() + 1);
    return { start: fmt(tmrw), end: fmt(tmrw) };
  }
  if (preset === 'Yesterday') {
    const yest = new Date(date); yest.setDate(yest.getDate() - 1);
    return { start: fmt(yest), end: fmt(yest) };
  }
  if (preset === 'This Week') {
    const day = date.getDay(); const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(date); start.setDate(diff);
    const end = new Date(start); end.setDate(end.getDate() + 6);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === 'Next Week') {
    const day = date.getDay(); const diff = date.getDate() - day + (day === 0 ? -6 : 1) + 7;
    const start = new Date(date); start.setDate(diff);
    const end = new Date(start); end.setDate(end.getDate() + 6);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === 'This Month') {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { start: fmt(start), end: fmt(end) };
  }
  if (preset === 'Last 7 Days') {
    const start = new Date(date); start.setDate(date.getDate() - 7);
    return { start: fmt(start), end: fmt(date) }; // To today
  }
  return { start: '', end: '' };
};

// --- Sub Components ---

const OrderItemsList = ({ items, limit, detailed = false }) => {
  const displayItems = limit ? items?.slice(0, limit) : items;
  if (!displayItems || displayItems.length === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>No items</p>;

  return (
    <ul className="items-list-preview">
      {displayItems.map((item, idx) => {
        const itemPrice = parseFloat(item.item_price || item.unitPrice || 0);
        const itemQty = parseInt(item.quantity || item.qty || 1, 10);
        const lineTotal = itemPrice * itemQty;

        return (
          <li key={idx} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span className="qty-badge" style={{ alignSelf: 'flex-start' }}>{itemQty}x</span>
                <div className="item-text-group">
                  <span className="item-name-preview" style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                    {item.name || item.product_name}
                  </span>
                  {item.variant_name && (
                    <span className="item-variant-preview" style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: '6px' }}>
                      ({item.variant_name})
                    </span>
                  )}
                  {detailed && item.product_retailer_id && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                      Code: {item.product_retailer_id}
                    </div>
                  )}
                  {item.addon_items?.length > 0 && (
                    <div className="item-addons-preview" style={{ fontSize: '0.8rem', color: '#0f766e', marginTop: '4px' }}>
                      + Addons: {item.addon_items.map(a => `${a.name || a.addonitem_name}${a.price || a.addonitem_price ? ` (₹${a.price || a.addonitem_price})` : ''}`).join(', ')}
                    </div>
                  )}
                  {item.specialnotes && (
                    <div className="item-notes-preview" style={{ fontSize: '0.8rem', color: '#b45309', marginTop: '4px', fontStyle: 'italic' }}>
                      Note: {item.specialnotes}
                    </div>
                  )}
                  {detailed && item.item_discount > 0 && (
                    <div style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '2px' }}>
                      Discount: -₹{item.item_discount}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontWeight: '600', color: 'var(--text-main)' }}>{formatCurrency(lineTotal, 'INR')}</span>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                  @ {formatCurrency(itemPrice, 'INR')}
                </div>
              </div>
            </div>
          </li>
        );
      })}
      {limit && items.length > limit && (
        <li style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 'bold', paddingLeft: '2.5rem', borderBottom: 'none' }}>
          +{items.length - limit} more items...
        </li>
      )}
    </ul>
  );
};

const OrderCard = ({ order, onOpenModal, onAction, onOpenWhatsAppChat, onOpenAssignModal }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const getAddressString = () => {
    const addr = order.address || order.customer_details?.address;
    if (!addr) return 'No address provided';
    if (typeof addr === 'string') return addr;
    return [addr.address1, addr.address2, addr.city, addr.zip].filter(Boolean).join(', ');
  };

  const paidAmount = order.payment_details?.amount?.value ? order.payment_details.amount.value / 100 : order.total_amount;

  let orderTypeLabel = 'Home Delivery';
  const rawType = order.order_type?.toUpperCase();
  if (rawType === 'P' || rawType === 'PARCEL' || rawType === 'PICKUP') orderTypeLabel = 'Pickup';
  if (rawType === 'D' || rawType === 'DINEIN') orderTypeLabel = 'Dine-In';
  if (rawType === 'H' || rawType === 'HOME_DELIVERY') orderTypeLabel = 'Home Delivery';

  const loc = getLatLon(order.address);
  const hasLocation = !!loc;
  const isPendingApproval = order.approval_status === 'PENDING_APPROVAL' || order.status === 'pending' || order.status === 'processing' || order.status === 'draft';
  const needsPayment = order.payment_status !== 'PAID';
  const phoneToUse = order.customer_details?.phone_number || order.customer_phone;

  const renderTimeCounter = () => {
    const lowerStatus = order.status?.toLowerCase();
    if (lowerStatus === 'delivered' || lowerStatus === 'cancelled') return null;

    if (!order.slot_start_time) return null;
    const slotStart = new Date(order.slot_start_time);
    const now = new Date();
    const diffMs = slotStart.getTime() - now.getTime();
    const diffHrs = diffMs / (1000 * 60 * 60);

    if (diffHrs > 24) return null;

    let badgeClass = '';
    let label = '';

    const absMs = Math.abs(diffMs);
    const h = Math.floor(absMs / (1000 * 60 * 60));
    const m = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const prefix = diffMs < 0 ? '-' : '';
    label = `${prefix}${h}h ${m}m`;

    if (diffMs < 0) {
      badgeClass = 'past-due';
    } else if (diffHrs <= 1) {
      badgeClass = 'next-1h';
    } else if (diffHrs <= 6) {
      badgeClass = 'next-6h';
    } else {
      badgeClass = 'future';
    }

    return (
      <div className={`delivery-countdown-badge ${badgeClass}`}>
        ⏱️ {label}
      </div>
    );
  };

  const isCancelled = order.status?.toLowerCase() === 'cancelled';
  const isDelivered = order.status?.toLowerCase() === 'delivered';

  return (
    <div className="lux-card" onClick={() => onOpenModal(order)}>
      <div className="card-top">
        <div>
          <div className="order-id">#{order.id}</div>
          <div className="card-meta">
            {order.delivery_date && (
              <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                🚚 {formatDateNoYear(order.delivery_date, false)}
                {order.delivery_slot_label ? ` (${order.delivery_slot_label})` : (order.slot_start_time && order.slot_end_time && ` (${formatSlotTime(order.slot_start_time, order.slot_end_time)})`)}
              </div>
            )}
            {renderTimeCounter()}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="card-chips">
            <span className="card-amount-pill">{formatCurrency(paidAmount, order.currency)}</span>
            <span className="lux-chip chip-status">{order.status}</span>
            <span className={`lux-chip ${order.payment_status === 'PAID' ? 'chip-payment' : 'chip-unpaid'}`}>{order.payment_status || 'PENDING'}</span>
          </div>

          <div className="three-dot-menu-container" ref={menuRef} onClick={(e) => e.stopPropagation()}>
            <button
              className="three-dot-btn"
              title="More Options"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu((prev) => !prev);
              }}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="three-dot-dropdown">
                {!isCancelled && !isDelivered && (
                  <button
                    className="dropdown-item danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onAction(order, 'reject');
                    }}
                  >
                    🚫 Reject Order
                  </button>
                )}
                <button
                  className="dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onOpenModal(order);
                  }}
                >
                  👁️ View Details
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-middle">
        <div className="customer-info">
          <div className="customer-avatar">{order.customer_details?.name?.charAt(0) || order.customer_name?.charAt(0) || 'G'}</div>
          <div className="customer-details">
            <h3>{order.customer_details?.name || order.customer_name || 'Guest'}</h3>
            <p>{phoneToUse}</p>
          </div>
          <button className="icon-btn" onClick={(e) => { e.stopPropagation(); onOpenWhatsAppChat(phoneToUse); }}>💬</button>
        </div>

        {order.description && (
          <div className="order-note-bubble" style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', borderLeft: '3px solid var(--primary)', padding: '0.4rem 0.6rem', borderRadius: '4px', margin: '0.5rem 0' }}>
            📝 <strong>Note:</strong> {order.description}
          </div>
        )}

        {orderTypeLabel === 'Home Delivery' && (
          <div className="address-box">
            <div className="address-text">
              <span style={{ fontSize: '1rem' }}>📍</span>
              <span>{getAddressString()}</span>
            </div>
            {!hasLocation ? (
              <button className="btn-loc request" onClick={(e) => { e.stopPropagation(); onAction(order, 'request_location'); }}>Request Location</button>
            ) : (
              <button className="btn-loc directions" onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps?q=${loc.lat},${loc.lon}`, '_blank'); }}>🧭 Directions</button>
            )}
          </div>
        )}
        {orderTypeLabel === 'Dine-In' && (
          <div className="address-box">
            <div className="address-text">
              <span style={{ fontSize: '1rem' }}>🍽️</span>
              <span style={{ fontWeight: '600' }}>Dine-In (Table No: {order.table_no || 'None'})</span>
            </div>
          </div>
        )}
        {orderTypeLabel === 'Pickup' && (
          <div className="address-box">
            <div className="address-text">
              <span style={{ fontSize: '1rem' }}>🛍️</span>
              <span style={{ fontWeight: '600' }}>Pickup Order</span>
            </div>
          </div>
        )}
      </div>

      <div className="items-section">
        <OrderItemsList items={order.product_items?.product_items} limit={3} />
      </div>

      <div className="card-bottom-actions">
        <div style={{ display: 'flex', width: '100%', gap: '0.6rem', flexDirection: 'row' }}>
          {isPendingApproval && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onAction(order, 'reject'); }} className="btn-action btn-reject">Reject</button>
              <button onClick={(e) => { e.stopPropagation(); onAction(order, 'approve'); }} className="btn-action btn-approve">Approve</button>
            </>
          )}
          {needsPayment && <button onClick={(e) => { e.stopPropagation(); onAction(order, 'request_payment'); }} className="btn-action btn-payment">Request Pay</button>}
          {order.status === 'confirmed' && <button onClick={(e) => { e.stopPropagation(); onAction(order, 'mark_ready'); }} className="btn-action btn-ready">Mark Ready</button>}

          {order.status === 'ready_for_pickup' && orderTypeLabel === 'Home Delivery' && <button onClick={(e) => { e.stopPropagation(); onOpenAssignModal(order); }} className="btn-action btn-dispatched">Dispatch / Pick Up</button>}

          {order.status === 'ready_for_pickup' && (orderTypeLabel === 'Dine-In' || orderTypeLabel === 'Pickup') && <button onClick={(e) => { e.stopPropagation(); onAction(order, 'delivered'); }} className="btn-action btn-delivered">Mark Completed</button>}

          {order.status === 'out_for_delivery' && <button onClick={(e) => { e.stopPropagation(); onAction(order, 'delivered'); }} className="btn-action btn-delivered">Deliver</button>}
        </div>
      </div>
    </div>
  );
};

const CreateOrderItemDrawer = ({ item, addongroups, onClose, onAddToCart }) => {
  const hasVariations = String(item.itemallowvariation) === '1' && item.variation?.length > 0;
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [note, setNote] = useState('');
  const [qty, setQty] = useState(1);

  const applicableAddonGroupIds = useCallback(() => {
    if (hasVariations && selectedVariation) {
      return (selectedVariation.addon || []).map((a) => a.addon_group_id);
    }
    return (item.addon || []).map((a) => a.addon_group_id);
  }, [hasVariations, selectedVariation, item.addon]);

  const applicableGroups = addongroups.filter((g) => applicableAddonGroupIds().includes(g.addongroupid));

  const toggleAddon = (groupId, groupName, addonItem) => {
    const key = `${groupId}_${addonItem.addonitemid}`;
    const next = selectedAddons.filter((a) => `${a.group_id}_${a.id}` !== key);
    if (next.length === selectedAddons.length) {
      next.push({
        group_id: groupId,
        group_name: groupName,
        id: addonItem.addonitemid,
        name: addonItem.addonitem_name,
        price: addonItem.addonitem_price,
        quantity: 1,
      });
    }
    setSelectedAddons(next);
  };

  const isAddonSelected = (groupId, addonId) => selectedAddons.some((a) => a.group_id === groupId && a.id === addonId);
  const computeBasePrice = () => (hasVariations && selectedVariation ? parseFloat(selectedVariation.price || 0) : parseFloat(item.price || 0));
  const addonTotal = selectedAddons.reduce((sum, a) => sum + parseFloat(a.price || 0) * (a.quantity || 1), 0);
  const totalPrice = (computeBasePrice() + addonTotal) * qty;
  const canAdd = !hasVariations || selectedVariation !== null;

  const handleAdd = () => {
    const cartKey = makeCartKey(item, hasVariations ? selectedVariation : null);
    const itemId = hasVariations && selectedVariation ? selectedVariation.id : item.itemid;
    const variationLabel = hasVariations && selectedVariation ? selectedVariation.name : null;
    const itemName = hasVariations && selectedVariation
      ? `${item.itemname} (${selectedVariation.name})`
      : item.itemname;

    onAddToCart({
      cartKey,
      itemId: String(itemId),
      baseItemId: String(item.itemid),
      itemName,
      displayName: item.itemname,
      variationName: variationLabel,
      addonLabel: selectedAddons.map((a) => a.name).join(', '),
      addonItems: selectedAddons,
      note,
      qty,
      unitPrice: computeBasePrice(),
      addonPrice: addonTotal,
      totalPrice,
    });
    onClose();
  };

  return (
    <>
      <div className="di-overlay" onClick={onClose} />
      <div className="di-drawer" role="dialog" aria-modal="true">
        <div className="di-drawer-handle" />
        <div className="di-drawer-head">
          <h2>{item.itemname}</h2>
          <button className="di-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="di-drawer-body">
          {hasVariations && (
            <>
              <div className="di-label-pill">Choose Size / Variant</div>
              <div className="di-variations-grid">
                {item.variation.filter((v) => isActive(v.active)).map((v) => (
                  <div
                    key={v.id}
                    className={`di-var-card ${selectedVariation?.id === v.id ? 'selected' : ''}`}
                    onClick={() => { setSelectedVariation(v); setSelectedAddons([]); }}
                  >
                    <span className="di-var-card-name">{v.name}</span>
                    <span className="di-var-card-price">{rupee(v.price)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {applicableGroups.length > 0 && (
            <>
              <div className="di-label-pill" style={{ marginTop: 20 }}>Customise</div>
              {applicableGroups.map((group) => (
                <div key={group.addongroupid} className="di-addon-group">
                  <div className="di-addon-group-name">{group.addongroup_name}</div>
                  {group.addongroupitems.filter((ai) => isActive(ai.active)).map((ai) => (
                    <div
                      key={ai.addonitemid}
                      className={`di-addon-item ${isAddonSelected(group.addongroupid, ai.addonitemid) ? 'selected' : ''}`}
                      onClick={() => toggleAddon(group.addongroupid, group.addongroup_name, ai)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <span className="di-addon-item-name">{ai.addonitem_name}</span>
                      </div>
                      {parseFloat(ai.addonitem_price) > 0 && <span className="di-addon-item-price">+{rupee(ai.addonitem_price)}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
          <div className="di-label-pill" style={{ marginTop: applicableGroups.length > 0 ? 8 : 20 }}>Special Instructions</div>
          <textarea className="di-special-note" rows={2} placeholder="e.g. Less spicy, no onion..." value={note} onChange={(e) => setNote(e.target.value)} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Quantity</span>
            <div className="di-counter">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))}>-</button>
              <span>{qty}</span>
              <button onClick={() => setQty((q) => q + 1)}>+</button>
            </div>
          </div>
        </div>
        <div className="di-drawer-footer">
          <button className="di-btn-primary" onClick={handleAdd} disabled={!canAdd}>
            {canAdd ? `Add to Order - ${rupee(totalPrice)}` : 'Select a variant to continue'}
          </button>
        </div>
      </div>
    </>
  );
};

const CreateOrderCartDrawer = ({ cart, onClose, onCartAction, onCheckout }) => {
  const cartItems = Object.values(cart);
  const subtotal = cartItems.reduce((s, i) => s + i.totalPrice, 0);
  return (
    <>
      <div className="di-overlay" onClick={onClose} />
      <div className="di-drawer" role="dialog" aria-modal="true">
        <div className="di-drawer-handle" />
        <div className="di-drawer-head">
          <h2>Your Order</h2>
          <button className="di-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="di-drawer-body">
          {cartItems.map((ci) => (
            <div key={ci.cartKey} className="di-cart-item-row">
              <div className="di-cart-item-info">
                <div className="di-cart-item-name">{ci.displayName}</div>
                <div className="di-cart-item-sub">
                  {ci.variationName && <span>{ci.variationName} · </span>}
                  {ci.addonLabel && <span>{ci.addonLabel} · </span>}
                  {ci.note && <span>"{ci.note}"</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="di-counter">
                  <button onClick={() => onCartAction('decrement', ci)}>-</button>
                  <span>{ci.qty}</span>
                  <button onClick={() => onCartAction('increment', ci)}>+</button>
                </div>
                <div className="di-cart-item-price">{rupee(ci.totalPrice)}</div>
                <button onClick={() => onCartAction('remove', ci)} className="checkout-remove-btn">x</button>
              </div>
            </div>
          ))}
        </div>
        {cartItems.length > 0 && (
          <div className="di-drawer-footer">
            <button className="di-btn-primary" onClick={onCheckout}>
              Place Order - {rupee(subtotal)}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default function OrdersPage({ token, business_id, businessName }) {
  const navigate = useNavigate();
  const fetchingRef = useRef(false);
  const [allOrders, setAllOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [activeTab, setActiveTab] = useState('OPERATIONS');
  const [opsSubTab, setOpsSubTab] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);

  const todayIST = useMemo(() => new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)), []);
  const todayStr = useMemo(() => getLocalDateString(todayIST), [todayIST]);

  // Global Order Date State (Fetches API)
  const [dateFilterType, setDateFilterType] = useState('delivery_date');
  const initialGlobalRange = useMemo(() => {
    const fmt = getLocalDateString;
    const start = new Date(todayIST.getTime());
    start.setDate(start.getDate() - 7);
    const end = new Date(todayIST.getTime());
    end.setDate(end.getDate() + 30);
    return { start: fmt(start), end: fmt(end) };
  }, [todayIST]);

  const [globalOrderDatePreset, setGlobalOrderDatePreset] = useState('Active Orders');
  const [apiStartDate, setApiStartDate] = useState(initialGlobalRange.start);
  const [apiEndDate, setApiEndDate] = useState(initialGlobalRange.end);

  // Local Default Filters (removed order dates)
  const defaultDraft = { deliveryPreset: 'All', deliveryStartDate: '', deliveryEndDate: '', paymentStatus: '', approvalStatus: '', timeslot: '', deliveryType: '' };
  const defaultOps = { deliveryPreset: 'All', deliveryStartDate: '', deliveryEndDate: '', deliveryType: '', timeslot: '' };
  const defaultArchive = { status: '', deliveryPreset: 'All', deliveryStartDate: '', deliveryEndDate: '', deliveryType: '', timeslot: '' };

  const [draftFilters, setDraftFilters] = useState({ ...defaultDraft });
  const [opsFilters, setOpsFilters] = useState({ ...defaultOps });
  const [archiveFilters, setArchiveFilters] = useState({ ...defaultArchive });

  const [loading, setLoading] = useState(false);
  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState('562584336938922');
  const [photoRequiredForOrderReady, setPhotoRequiredForOrderReady] = useState(false);
  const [drivers, setDrivers] = useState([]);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showMarkReadyModal, setShowMarkReadyModal] = useState(false);
  const [orderToMarkReady, setOrderToMarkReady] = useState(null);

  // Assign Driver Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [orderToAssign, setOrderToAssign] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');

  const [mediaId, setMediaId] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [menuData, setMenuData] = useState(null);
  const [activeCreateCat, setActiveCreateCat] = useState(null);
  const [createItemDrawer, setCreateItemDrawer] = useState(null);
  const [createCartOpen, setCreateCartOpen] = useState(false);
  const [createCheckoutOpen, setCreateCheckoutOpen] = useState(false);
  const [createCart, setCreateCart] = useState({});
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createCustomName, setCreateCustomName] = useState('');
  const [createCustomPrice, setCreateCustomPrice] = useState('');
  const [createCustomQty, setCreateCustomQty] = useState(1);
  const [checkoutForm, setCheckoutForm] = useState({
    phone_number: '',
    name: '',
    orderType: '',
    delivery_date: '',
    delivery_slot: '',
    delivery_slot_id: '',
    address: '',
    pincode: '',
    description: '',
    deliveryProviderId: '',
    pickupLocationId: '',
    deliveryCharges: 0,
  });

  // Reject Modal State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [orderToReject, setOrderToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectAmount, setRejectAmount] = useState('');
  const [rejectSpeed, setRejectSpeed] = useState('normal');

  // Edit Order Modal States
  const [isEditing, setIsEditing] = useState(false);
  const [editDeliveryCache, setEditDeliveryCache] = useState({
    HOME_DELIVERY: {
      address1: '',
      address2: '',
      city: '',
      province: '',
      zip: '',
      country: '',
      deliveryProviderId: '',
      delivery_charges: 0,
      slot_start_time: '',
      slot_end_time: '',
      delivery_slot_id: '',
      delivery_date: ''
    },
    PICKUP: {
      pickupLocationId: '',
      slot_start_time: '',
      slot_end_time: '',
      delivery_slot_id: '',
      delivery_date: ''
    }
  });
  const [editForm, setEditForm] = useState({
    status: '',
    order_type: '',
    customer_name: '',
    customer_phone: '',
    delivery_date: '',
    slot_start_time: '',
    slot_end_time: '',
    delivery_slot_id: '',
    delivery_charges: 0,
    description: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: '',
    table_no: '',
    deliveryProviderId: '',
    pickupLocationId: '',
    product_items: []
  });
  const [editCustomName, setEditCustomName] = useState('');
  const [editCustomPrice, setEditCustomPrice] = useState('');
  const [editCustomQty, setEditCustomQty] = useState(1);
  const [businessInfo, setBusinessInfo] = useState(null);

  useEffect(() => {
    if (!token) return;
    const fetchBusinessInfo = async () => {
      try {
        const businessId = localStorage.getItem('businessId');
        if (businessId) {
          const response = await QueueService.GetBusinessInfo(parseInt(businessId, 10));
          setBusinessInfo(response);
          setWhatsappPhoneNumberId(response.whatsapp_phone_number_id || '562584336938922');
          setPhotoRequiredForOrderReady(response.business_metadata?.photo_required_for_order_ready === true);

          // Fetch Drivers
          const drvRes = await fetch(`${BASE_URL}/${businessId}/employees?role=driver`, { headers: { Authorization: `Bearer ${token}` } });
          const drvData = await drvRes.json();
          if (drvData && drvData.success && Array.isArray(drvData.data)) {
            setDrivers(drvData.data);
          } else if (Array.isArray(drvData)) {
            setDrivers(drvData);
          }
        }
      } catch (e) { }
    };
    fetchBusinessInfo();
  }, [token]);

  const addCreateCustomItem = () => {
    if (!createCustomName) return alert('Please enter item name');
    const price = parseFloat(createCustomPrice) || 0;
    const qty = parseInt(createCustomQty, 10) || 1;
    const customId = `custom_${Date.now()}`;
    const newItem = {
      cartKey: `${customId}_simple`,
      itemId: customId,
      itemName: createCustomName,
      displayName: createCustomName,
      variationName: null,
      addonLabel: '',
      addonItems: [],
      note: '',
      qty: qty,
      unitPrice: price,
      addonPrice: 0,
      totalPrice: price * qty
    };

    setCreateCart((prev) => {
      const ex = prev[newItem.cartKey];
      if (!ex) return { ...prev, [newItem.cartKey]: newItem };
      const qtyCombined = ex.qty + newItem.qty;
      return { ...prev, [newItem.cartKey]: { ...ex, qty: qtyCombined, totalPrice: (ex.unitPrice + ex.addonPrice) * qtyCombined } };
    });

    setCreateCustomName('');
    setCreateCustomPrice('');
    setCreateCustomQty(1);
    alert('Custom item added to cart!');
  };

  const fetchOrders = useCallback(async () => {
    if (!token || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      let finalStart = apiStartDate;
      let finalEnd = apiEndDate;

      if (!apiStartDate && !apiEndDate) {
        finalStart = '';
        finalEnd = '';
      } else if (apiEndDate) {
        const [y, m, d] = apiEndDate.split('-').map(Number);
        const nextDay = new Date(y, m - 1, d + 1);
        finalEnd = getLocalDateString(nextDay);
      }

      let url = `${BASE_URL}/orders?auth_token=${token}`;
      if (dateFilterType === 'delivery_date') {
        if (finalStart) url += `&delivery_date_start=${finalStart}`;
        if (finalEnd) url += `&delivery_date_end=${finalEnd}`;
      } else {
        if (finalStart) url += `&created_at_start=${finalStart}`;
        if (finalEnd) url += `&created_at_end=${finalEnd}`;
      }

      const response = await fetch(url);
      if (response.status === 401) { window.location.href = '/'; return; }
      const data = await response.json();
      if (data.success) setAllOrders(data.data);
    } catch (e) { }
    setLoading(false);
    fetchingRef.current = false;
  }, [token, apiStartDate, apiEndDate, dateFilterType]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const activeCreateCategories = useMemo(
    () => (menuData?.categories || []).filter((c) => isActive(c.active)),
    [menuData]
  );
  const createAddongroups = useMemo(() => menuData?.addongroups || [], [menuData]);
  const createMenuItems = useMemo(() => menuData?.items || [], [menuData]);
  const createCartItems = useMemo(() => Object.values(createCart), [createCart]);
  const createCartTotal = useMemo(() => createCartItems.reduce((sum, item) => sum + item.totalPrice, 0), [createCartItems]);
  const createCartQty = useMemo(() => createCartItems.reduce((sum, item) => sum + item.qty, 0), [createCartItems]);

  const itemsForCreateCategory = useCallback((categoryId) => (
    createMenuItems.filter((item) => item.item_categoryid === categoryId && isActive(item.active))
  ), [createMenuItems]);

  const openCreateOrderFlow = async () => {
    setShowCreateOrderModal(true);
    setMenuLoading(true);
    setMenuError('');
    try {
      const response = await fetch(CATALOG_EXPORT_URL, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
      const raw = await response.json();
      const parsed = parseMenuResponse(raw);
      if (parsed.success === '1' || parsed.success === true || raw.success === true) {
        const normalized = normalizePetpoojaMenu(parsed);
        setMenuData(normalized);
        const first = (normalized.categories || []).find((c) => isActive(c.active)) || normalized.categories?.[0];
        setActiveCreateCat(first?.categoryid || null);
      } else {
        setMenuError(parsed.message || raw.message || 'Unable to load menu');
      }
    } catch (error) {
      setMenuError('Unable to load menu');
    } finally {
      setMenuLoading(false);
    }
  };

  const closeCreateOrderFlow = () => {
    setShowCreateOrderModal(false);
    setCreateItemDrawer(null);
    setCreateCartOpen(false);
    setCreateCheckoutOpen(false);
  };

  const addCreateCartItem = (cartItem) => {
    setCreateCart((prev) => {
      const existing = prev[cartItem.cartKey];
      if (!existing) return { ...prev, [cartItem.cartKey]: cartItem };
      const newQty = existing.qty + cartItem.qty;
      return {
        ...prev,
        [cartItem.cartKey]: {
          ...existing,
          qty: newQty,
          totalPrice: (existing.unitPrice + existing.addonPrice) * newQty,
        },
      };
    });
  };

  const onCreateCartAction = (action, ci) => {
    if (action === 'remove') {
      setCreateCart((prev) => { const next = { ...prev }; delete next[ci.cartKey]; return next; });
      return;
    }
    if (action === 'increment') {
      setCreateCart((prev) => ({
        ...prev,
        [ci.cartKey]: { ...prev[ci.cartKey], qty: prev[ci.cartKey].qty + 1, totalPrice: (prev[ci.cartKey].unitPrice + prev[ci.cartKey].addonPrice) * (prev[ci.cartKey].qty + 1) },
      }));
      return;
    }
    if (action === 'decrement') {
      setCreateCart((prev) => {
        const existing = prev[ci.cartKey];
        if (!existing) return prev;
        const nextQty = existing.qty - 1;
        if (nextQty <= 0) { const next = { ...prev }; delete next[ci.cartKey]; return next; }
        return {
          ...prev,
          [ci.cartKey]: { ...existing, qty: nextQty, totalPrice: (existing.unitPrice + existing.addonPrice) * nextQty },
        };
      });
    }
  };

  const handleCheckoutPincodeChange = (pincodeVal) => {
    const supported = getSupportedProviders(businessInfo, pincodeVal);
    let providerId = '';
    let fee = 0;
    if (supported.length === 1) {
      providerId = supported[0].id;
      fee = calculateProviderDeliveryPrice(supported[0], pincodeVal);
    }
    setCheckoutForm((prev) => ({
      ...prev,
      pincode: pincodeVal,
      deliveryProviderId: providerId,
      deliveryCharges: fee,
      delivery_slot: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));
  };

  const handleCheckoutOrderTypeChange = (typeVal) => {
    setCheckoutForm((prev) => ({
      ...prev,
      orderType: typeVal,
      delivery_date: '',
      delivery_slot: '',
      delivery_slot_id: '',
      deliveryProviderId: '',
      pickupLocationId: '',
      deliveryCharges: 0
    }));
  };

  const handleCheckoutProviderChange = (providerId) => {
    const supported = getSupportedProviders(businessInfo, checkoutForm.pincode);
    const provider = supported.find(p => p.id === providerId);
    const fee = provider ? calculateProviderDeliveryPrice(provider, checkoutForm.pincode) : 0;
    setCheckoutForm((prev) => ({
      ...prev,
      deliveryProviderId: providerId,
      deliveryCharges: fee,
      delivery_slot: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));
  };

  const handleCheckoutPickupLocationChange = (locationId) => {
    setCheckoutForm((prev) => ({
      ...prev,
      pickupLocationId: locationId,
      delivery_slot: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));
  };

  const handleEditPincodeChange = (pincodeVal) => {
    const supported = getSupportedProviders(businessInfo, pincodeVal);
    let providerId = '';
    let fee = 0;
    if (supported.length === 1) {
      providerId = supported[0].id;
      fee = calculateProviderDeliveryPrice(supported[0], pincodeVal);
    }

    setEditForm(prev => ({
      ...prev,
      zip: pincodeVal,
      deliveryProviderId: providerId,
      delivery_charges: fee,
      slot_start_time: '',
      slot_end_time: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));

    setEditDeliveryCache(c => ({
      ...c,
      HOME_DELIVERY: {
        ...c.HOME_DELIVERY,
        zip: pincodeVal,
        deliveryProviderId: providerId,
        delivery_charges: fee,
        slot_start_time: '',
        slot_end_time: '',
        delivery_slot_id: '',
        delivery_date: ''
      }
    }));
  };

  const handleEditProviderChange = (providerId) => {
    const supported = getSupportedProviders(businessInfo, editForm.zip);
    const provider = supported.find(p => p.id === providerId);
    const fee = provider ? calculateProviderDeliveryPrice(provider, editForm.zip) : 0;

    setEditForm(prev => ({
      ...prev,
      deliveryProviderId: providerId,
      delivery_charges: fee,
      slot_start_time: '',
      slot_end_time: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));

    setEditDeliveryCache(c => ({
      ...c,
      HOME_DELIVERY: {
        ...c.HOME_DELIVERY,
        deliveryProviderId: providerId,
        delivery_charges: fee,
        slot_start_time: '',
        slot_end_time: '',
        delivery_slot_id: '',
        delivery_date: ''
      }
    }));
  };

  const handleEditPickupLocationChange = (locationId) => {
    setEditForm(prev => ({
      ...prev,
      pickupLocationId: locationId,
      slot_start_time: '',
      slot_end_time: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));

    setEditDeliveryCache(c => ({
      ...c,
      PICKUP: {
        ...c.PICKUP,
        pickupLocationId: locationId,
        slot_start_time: '',
        slot_end_time: '',
        delivery_slot_id: '',
        delivery_date: ''
      }
    }));
  };

  const handleEditOrderTypeChange = (typeVal) => {
    const targetMode = typeVal;
    if (targetMode === 'HOME_DELIVERY') {
      const cached = editDeliveryCache.HOME_DELIVERY;
      setEditForm(prev => ({
        ...prev,
        order_type: targetMode,
        address1: cached.address1,
        address2: cached.address2,
        city: cached.city,
        province: cached.province,
        zip: cached.zip,
        country: cached.country,
        deliveryProviderId: cached.deliveryProviderId,
        delivery_charges: cached.delivery_charges,
        slot_start_time: cached.slot_start_time,
        slot_end_time: cached.slot_end_time,
        delivery_slot_id: cached.delivery_slot_id,
        delivery_date: cached.delivery_date
      }));
    } else if (targetMode === 'PICKUP') {
      const cached = editDeliveryCache.PICKUP;
      setEditForm(prev => ({
        ...prev,
        order_type: targetMode,
        pickupLocationId: cached.pickupLocationId,
        delivery_charges: 0,
        slot_start_time: cached.slot_start_time,
        slot_end_time: cached.slot_end_time,
        delivery_slot_id: cached.delivery_slot_id,
        delivery_date: cached.delivery_date
      }));
    } else {
      // DINE_IN
      setEditForm(prev => ({
        ...prev,
        order_type: targetMode,
        delivery_charges: 0,
        slot_start_time: '',
        slot_end_time: '',
        delivery_slot_id: '',
        delivery_date: ''
      }));
    }
  };

  const createOrderCheckout = async () => {
    if (!checkoutForm.phone_number || !checkoutForm.name) { alert('Please enter customer name and phone number'); return; }
    if (!checkoutForm.delivery_date || !checkoutForm.delivery_slot) { alert('Please select delivery date and slot'); return; }
    if (checkoutForm.orderType === 'HOME_DELIVERY' && !checkoutForm.address.trim()) { alert('Please enter address for home delivery'); return; }
    if (checkoutForm.orderType === 'HOME_DELIVERY' && !checkoutForm.pincode.trim()) { alert('Please enter pin code for home delivery'); return; }
    if (checkoutForm.orderType === 'PICKUP' && !checkoutForm.pickupLocationId) { alert('Please select pickup location'); return; }
    if (createCartItems.length === 0) { alert('Please add products first'); return; }

    const [slotStart, slotEnd] = checkoutForm.delivery_slot.split('-');
    const businessId = parseInt(localStorage.getItem('businessId') || '2', 10);

    const constructISODatetime = (dateStr, timeStr) => {
      if (!dateStr || !timeStr) return null;
      if (timeStr.includes('T')) return timeStr;
      return `${dateStr}T${timeStr.substring(0, 5)}:00+05:30`;
    };

    const selectedPickupLocation = checkoutForm.orderType === 'PICKUP'
      ? businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup?.locations?.find(l => l.id === checkoutForm.pickupLocationId)
      : null;
    const address1Text = selectedPickupLocation
      ? selectedPickupLocation.address
      : checkoutForm.address || 'Pickup from store';

    const getSlotsForSelection = () => {
      if (checkoutForm.orderType === 'HOME_DELIVERY') {
        if (!checkoutForm.deliveryProviderId) return [];
        const schedulingConfig = businessInfo?.business_metadata?.order_rules?.scheduling_config || businessInfo?.business_metadata?.scheduling_config;
        const providers = schedulingConfig?.methods?.local_delivery?.providers || [];
        const provider = providers.find(p => p.id === checkoutForm.deliveryProviderId);
        const slots = getProviderSlots(provider);
        return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
      } else if (checkoutForm.orderType === 'PICKUP') {
        if (!checkoutForm.pickupLocationId) return [];
        const slots = getPickupSlots(businessInfo, checkoutForm.pickupLocationId);
        return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
      }
      return [];
    };

    const slots = getSlotsForSelection();
    const selectedSlot = slots.find(s => s.value === checkoutForm.delivery_slot);

    const deliverySlotLabelVal = (() => {
      if (checkoutForm.orderType === 'HOME_DELIVERY') {
        const supported = getSupportedProviders(businessInfo, checkoutForm.pincode);
        const provider = supported.find(p => p.id === checkoutForm.deliveryProviderId);
        return selectedSlot && provider ? `${provider.label}: ${selectedSlot.label}` : (selectedSlot ? selectedSlot.label : null);
      } else {
        const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
        const loc = pickup.locations?.find(l => l.id === checkoutForm.pickupLocationId);
        return selectedSlot && loc ? `${loc.label}: ${selectedSlot.label}` : (selectedSlot ? selectedSlot.label : null);
      }
    })();

    const payload = {
      phone_number: checkoutForm.phone_number,
      name: checkoutForm.name,
      order_details: {
        business_id: businessId,
        total_amount: parseFloat((createCartTotal + (parseFloat(checkoutForm.deliveryCharges) || 0)).toFixed(2)),
        currency: 'INR',
        reference_id: buildOrderReference(),
        status: 'PENDING',
        source: 'WHATSAPP',
        text: 'Order created from frontend',
        description: checkoutForm.description,
        product_items: createCartItems.map((item) => ({
          product_retailer_id: item.itemId,
          product_name: item.itemName,
          quantity: item.qty,
          item_price: parseFloat(item.unitPrice.toFixed(2)),
          currency: 'INR',
          variant_name: item.variationName || '',
          final_price: parseFloat(item.unitPrice.toFixed(2)),
          item_discount: 0,
          addon_items: item.addonItems || [],
          specialnotes: item.note || '',
        })),
        address: {
          title: checkoutForm.orderType === 'HOME_DELIVERY' ? 'Home' : 'Pickup',
          description: address1Text,
          name: checkoutForm.name,
          address1: address1Text,
          zip: checkoutForm.pincode || '',
          delivery_provider_id: checkoutForm.orderType === 'HOME_DELIVERY' ? (checkoutForm.deliveryProviderId || null) : (checkoutForm.pickupLocationId || null),
          pickup_location_id: checkoutForm.orderType === 'PICKUP' ? (checkoutForm.pickupLocationId || null) : null,
        },
        gst_percentage: 0,
        packing_charges: 0,
        delivery_charges: parseFloat(checkoutForm.deliveryCharges) || 0,
        service_charges: 0,
        order_type: checkoutForm.orderType === 'PICKUP' ? 'PARCEL' : checkoutForm.orderType,
        payment_type: 'ONLINE',
        enable_delivery: checkoutForm.orderType === 'HOME_DELIVERY',
        delivery_by_business: true,
        delivery_date: checkoutForm.delivery_date,
        delivery_provider_id: checkoutForm.orderType === 'HOME_DELIVERY' ? (checkoutForm.deliveryProviderId || null) : (checkoutForm.pickupLocationId || null),
        delivery_slot_id: checkoutForm.delivery_slot_id || null,
        delivery_slot_label: deliverySlotLabelVal,
        slot_start_time: constructISODatetime(checkoutForm.delivery_date, slotStart),
        slot_end_time: constructISODatetime(checkoutForm.delivery_date, slotEnd),
      },
    };

    setCreatingOrder(true);
    try {
      const response = await fetch(CREATE_ORDER_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'Order creation failed');
      alert('Order created successfully');
      setCreateCart({});
      setCreateCheckoutOpen(false);
      setCreateCartOpen(false);
      setShowCreateOrderModal(false);
      fetchOrders();
    } catch (error) {
      alert(error.message || 'Order creation failed');
    } finally {
      setCreatingOrder(false);
    }
  };

  const startEditing = (order) => {
    const loc = order.address || {};
    const normalizedType = normalizeOrderType(order.order_type);
    const initialFee = (order.delivery_charges !== undefined && order.delivery_charges !== null && order.delivery_charges !== 0)
      ? order.delivery_charges
      : calculateDeliveryPrice(businessInfo, loc.zip || loc.pincode, normalizedType);

    const cache = {
      HOME_DELIVERY: {
        address1: normalizedType === 'HOME_DELIVERY' ? (loc.address1 || loc.description || (typeof order.address === 'string' ? order.address : '')) : '',
        address2: normalizedType === 'HOME_DELIVERY' ? (loc.address2 || '') : '',
        city: normalizedType === 'HOME_DELIVERY' ? (loc.city || '') : '',
        province: normalizedType === 'HOME_DELIVERY' ? (loc.province || loc.state || '') : '',
        zip: normalizedType === 'HOME_DELIVERY' ? (loc.zip || loc.pincode || '') : '',
        country: normalizedType === 'HOME_DELIVERY' ? (loc.country || '') : '',
        deliveryProviderId: normalizedType === 'HOME_DELIVERY' ? (order.delivery_provider_id || loc.delivery_provider_id || '') : '',
        delivery_charges: normalizedType === 'HOME_DELIVERY' ? initialFee : 0,
        slot_start_time: normalizedType === 'HOME_DELIVERY' && order.slot_start_time ? new Date(order.slot_start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
        slot_end_time: normalizedType === 'HOME_DELIVERY' && order.slot_end_time ? new Date(order.slot_end_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
        delivery_slot_id: normalizedType === 'HOME_DELIVERY' ? (order.delivery_slot_id || '') : '',
        delivery_date: normalizedType === 'HOME_DELIVERY' && order.delivery_date ? order.delivery_date.split('T')[0] : ''
      },
      PICKUP: {
        pickupLocationId: normalizedType === 'PICKUP' ? (order.delivery_provider_id || loc.pickup_location_id || loc.delivery_provider_id || '') : '',
        slot_start_time: normalizedType === 'PICKUP' && order.slot_start_time ? new Date(order.slot_start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
        slot_end_time: normalizedType === 'PICKUP' && order.slot_end_time ? new Date(order.slot_end_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
        delivery_slot_id: normalizedType === 'PICKUP' ? (order.delivery_slot_id || '') : '',
        delivery_date: normalizedType === 'PICKUP' && order.delivery_date ? order.delivery_date.split('T')[0] : ''
      }
    };
    setEditDeliveryCache(cache);

    setEditForm({
      status: order.status || 'pending',
      order_type: normalizedType,
      customer_name: order.customer_name || order.customer_details?.name || 'Guest',
      customer_phone: order.customer_phone || order.customer_details?.phone_number || '',
      delivery_date: order.delivery_date ? order.delivery_date.split('T')[0] : '',
      slot_start_time: order.slot_start_time ? new Date(order.slot_start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
      slot_end_time: order.slot_end_time ? new Date(order.slot_end_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
      delivery_slot_id: order.delivery_slot_id || '',
      delivery_charges: initialFee,
      description: order.description || '',
      address1: loc.address1 || loc.description || (typeof order.address === 'string' ? order.address : ''),
      address2: loc.address2 || '',
      city: loc.city || '',
      province: loc.province || loc.state || '',
      zip: loc.zip || loc.pincode || '',
      country: loc.country || '',
      table_no: order.table_no || '',
      deliveryProviderId: normalizedType === 'HOME_DELIVERY' ? (order.delivery_provider_id || loc.delivery_provider_id || '') : '',
      pickupLocationId: normalizedType === 'PICKUP' ? (order.delivery_provider_id || loc.pickup_location_id || loc.delivery_provider_id || '') : '',
      product_items: order.product_items?.product_items ? [...order.product_items.product_items] : []
    });
    setIsEditing(true);
  };

  const handleEditCartAction = (action, idx) => {
    const items = [...editForm.product_items];
    if (action === 'increment') {
      items[idx].quantity += 1;
    } else if (action === 'decrement') {
      items[idx].quantity -= 1;
      if (items[idx].quantity <= 0) {
        items.splice(idx, 1);
      }
    } else if (action === 'remove') {
      items.splice(idx, 1);
    }
    setEditForm(prev => ({ ...prev, product_items: items }));
  };

  const addEditCustomItem = () => {
    if (!editCustomName) return alert('Please enter item name');
    const price = parseFloat(editCustomPrice) || 0;
    const qty = parseInt(editCustomQty, 10) || 1;
    const newItem = {
      product_retailer_id: `custom_${Date.now()}`,
      product_name: editCustomName,
      name: editCustomName,
      quantity: qty,
      item_price: price,
      currency: 'INR',
      variant_name: '',
      final_price: price,
      item_discount: 0,
      addon_items: [],
      specialnotes: ''
    };
    setEditForm(prev => ({ ...prev, product_items: [...prev.product_items, newItem] }));
    setEditCustomName('');
    setEditCustomPrice('');
    setEditCustomQty(1);
  };

  const saveEditedOrder = async (orderId) => {
    const totalAmount = editForm.product_items.reduce((sum, item) => sum + (item.item_price * item.quantity), 0);
    const constructISODatetime = (dateStr, timeStr) => {
      if (!dateStr || !timeStr) return null;
      if (timeStr.includes('T')) return timeStr;
      return `${dateStr}T${timeStr.substring(0, 5)}:00+05:30`;
    };

    const selectedPickupLocation = editForm.order_type === 'PICKUP'
      ? businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup?.locations?.find(l => l.id === editForm.pickupLocationId)
      : null;
    const address1Text = selectedPickupLocation
      ? selectedPickupLocation.address
      : editForm.address1 || 'Pickup from store';

    const getEditSlotsForSelection = () => {
      if (editForm.order_type === 'HOME_DELIVERY') {
        if (!editForm.deliveryProviderId) return [];
        const schedulingConfig = businessInfo?.business_metadata?.order_rules?.scheduling_config || businessInfo?.business_metadata?.scheduling_config;
        const providers = schedulingConfig?.methods?.local_delivery?.providers || [];
        const provider = providers.find(p => p.id === editForm.deliveryProviderId);
        const slots = getProviderSlots(provider);
        return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
      } else if (editForm.order_type === 'PICKUP') {
        if (!editForm.pickupLocationId) return [];
        const slots = getPickupSlots(businessInfo, editForm.pickupLocationId);
        return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
      }
      return [];
    };

    const editSlots = getEditSlotsForSelection();
    const editSlotValStr = editForm.slot_start_time && editForm.slot_end_time ? `${editForm.slot_start_time}-${editForm.slot_end_time}` : '';
    const selectedEditSlot = editSlots.find(s => s.value === editSlotValStr);

    const editDeliverySlotLabelVal = (() => {
      if (editForm.order_type === 'HOME_DELIVERY') {
        const supported = getSupportedProviders(businessInfo, editForm.zip);
        const provider = supported.find(p => p.id === editForm.deliveryProviderId);
        return selectedEditSlot && provider ? `${provider.label}: ${selectedEditSlot.label}` : (selectedEditSlot ? selectedEditSlot.label : null);
      } else {
        const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
        const loc = pickup.locations?.find(l => l.id === editForm.pickupLocationId);
        return selectedEditSlot && loc ? `${loc.label}: ${selectedEditSlot.label}` : (selectedEditSlot ? selectedEditSlot.label : null);
      }
    })();

    const payload = {
      order_details: {
        order_type: editForm.order_type === 'PICKUP' ? 'PARCEL' : editForm.order_type,
        customer_name: editForm.customer_name,
        customer_phone: editForm.customer_phone,
        delivery_date: editForm.delivery_date || null,
        slot_start_time: constructISODatetime(editForm.delivery_date, editForm.slot_start_time),
        slot_end_time: constructISODatetime(editForm.delivery_date, editForm.slot_end_time),
        delivery_charges: parseFloat(editForm.delivery_charges) || 0,
        description: editForm.description,
        total_amount: parseFloat((totalAmount + (parseFloat(editForm.delivery_charges) || 0)).toFixed(2)),
        table_no: editForm.order_type === 'DINE_IN' ? editForm.table_no : null,
        delivery_provider_id: editForm.order_type === 'HOME_DELIVERY' ? (editForm.deliveryProviderId || null) : (editForm.pickupLocationId || null),
        delivery_slot_id: editForm.delivery_slot_id || null,
        delivery_slot_label: editDeliverySlotLabelVal,
        address: {
          title: editForm.order_type === 'HOME_DELIVERY' ? 'Home' : 'Pickup',
          name: editForm.customer_name,
          address1: address1Text,
          address2: editForm.address2 || '',
          city: editForm.city || '',
          province: editForm.province || '',
          zip: editForm.zip || '',
          country: editForm.country || '',
          description: address1Text,
          pincode: editForm.zip || '',
          delivery_provider_id: editForm.order_type === 'HOME_DELIVERY' ? (editForm.deliveryProviderId || null) : (editForm.pickupLocationId || null),
          pickup_location_id: editForm.order_type === 'PICKUP' ? (editForm.pickupLocationId || null) : null,
        },
        product_items: {
          product_items: editForm.product_items
        }
      }
    };

    try {
      const response = await fetch(`${BASE_URL}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'Failed to save changes');
      alert('Order updated successfully');
      setIsEditing(false);
      setSelectedOrder(data);
      fetchOrders();
    } catch (e) {
      alert(e.message || 'Failed to save changes');
    }
  };



  const handleDateFilterTypeChange = (newType) => {
    setDateFilterType(newType);
    if (newType === 'delivery_date') {
      setGlobalOrderDatePreset('Active Orders');
      const range = getPresetRange('Active Orders', todayIST);
      setApiStartDate(range.start);
      setApiEndDate(range.end);
    } else {
      setGlobalOrderDatePreset('Last 7 Days');
      const range = getPresetRange('Last 7 Days', todayIST);
      setApiStartDate(range.start);
      setApiEndDate(range.end);
    }
  };

  const applyGlobalDatePreset = (preset) => {
    setGlobalOrderDatePreset(preset);
    if (preset !== 'Custom') {
      const range = getPresetRange(preset, todayIST);
      setApiStartDate(range.start);
      setApiEndDate(range.end);
    }
  };

  const handleAction = async (order, actionType) => {
    if (actionType === 'mark_ready' && photoRequiredForOrderReady) {
      setOrderToMarkReady(order.id); setShowMarkReadyModal(true); return;
    }

    if (actionType === 'reject') {
      setOrderToReject(order);
      setRejectReason('');
      setRejectAmount('');
      setRejectSpeed('normal');
      setShowRejectModal(true);
      return;
    }

    try {
      let url = ''; let method = 'POST'; let body = null;
      if (actionType === 'request_location') url = `${BASE_URL}/orders/${order.id}/request_location`;
      else if (actionType === 'request_payment') url = `${BASE_URL}/orders/${order.id}/request_payment`;
      else if (actionType === 'approve') { url = `${BASE_URL}/orders/${order.id}/accept`; method = 'GET'; }
      else { method = actionType === 'mark_ready' ? 'POST' : 'GET'; url = `${BASE_URL}/orders/${order.id}/${actionType}`; }

      const response = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, ...(body && { body }) });
      const data = await response.json();
      if (data.success || data.id) {
        fetchOrders();
        if (selectedOrder?.id === order.id) setSelectedOrder(null);
      } else alert(data.message || `Action failed`);
    } catch (e) { alert("An error occurred"); }
  };

  const confirmRejectOrder = async () => {
    if (!orderToReject) return;
    if (!rejectReason.trim()) { alert("Please enter a reason for rejection"); return; }

    const isPaid = orderToReject.payment_status === 'PAID';
    const maxRefundAmount = orderToReject.payment_details?.amount?.value
      ? orderToReject.payment_details.amount.value / 100
      : (orderToReject.total_amount || 0);

    let amountNum = null;
    if (isPaid && rejectAmount !== '' && !isNaN(parseFloat(rejectAmount))) {
      amountNum = parseFloat(rejectAmount);
      if (amountNum < 0) {
        alert("Refund amount cannot be negative");
        return;
      }
      if (amountNum > maxRefundAmount) {
        alert(`Refund amount cannot exceed max amount of ₹${maxRefundAmount}`);
        return;
      }
    }

    try {
      const payload = {
        cancel_reason: rejectReason.trim(),
        speed: rejectSpeed || 'normal'
      };
      if (amountNum !== null && amountNum > 0) {
        payload.amount = amountNum;
      }

      const response = await fetch(`${BASE_URL}/orders/${orderToReject.id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success || data.id) {
        fetchOrders();
        setShowRejectModal(false);
        setOrderToReject(null);
        if (selectedOrder?.id === orderToReject.id) setSelectedOrder(null);
      } else {
        alert(data.message || "Failed to reject order");
      }
    } catch (e) { alert("Error rejecting order"); }
  };

  const confirmDispatch = async () => {
    if (!orderToAssign) return;
    try {
      const url = selectedDriverId
        ? `${BASE_URL}/orders/${orderToAssign.id}/out_for_delivery?employee_id=${selectedDriverId}`
        : `${BASE_URL}/orders/${orderToAssign.id}/out_for_delivery`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        fetchOrders();
        setShowAssignModal(false);
        setOrderToAssign(null);
        setSelectedDriverId('');
        if (selectedOrder?.id === orderToAssign.id) setSelectedOrder(null);
      } else {
        alert(data.message || 'Dispatch failed');
      }
    } catch (e) { alert("Error dispatching"); }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      setUploadingMedia(true);
      const response = await fetch(`${apiUrl}/whatsapp/upload_media`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      const result = await response.json();
      if (result.data?.id || result.id) { setMediaId(result.data?.id || result.id); setMediaPreviewUrl(URL.createObjectURL(file)); }
    } catch (err) { } finally { setUploadingMedia(false); }
  };

  const submitMarkReadyWithPhoto = async () => {
    if (!mediaId) return;
    try {
      await fetch(`${BASE_URL}/orders/${orderToMarkReady}/mark_ready?media_id=${mediaId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      fetchOrders(); setShowMarkReadyModal(false); setMediaId(null); setMediaPreviewUrl('');
      if (selectedOrder?.id === orderToMarkReady) setSelectedOrder(null);
    } catch (e) { }
  };

  // --- Filter Utils ---
  const applyDateRangePreset = (preset, fieldPrefix, filters, setFilters) => {
    if (preset === 'Custom') { setFilters({ ...filters, [`${fieldPrefix}Preset`]: preset }); return; }
    const range = getPresetRange(preset, todayIST);
    setFilters({ ...filters, [`${fieldPrefix}Preset`]: preset, [`${fieldPrefix}StartDate`]: range.start, [`${fieldPrefix}EndDate`]: range.end });
  };

  const renderActiveFilters = (filters, defaults, setFilters) => {
    const activeKeys = Object.keys(filters).filter(k => filters[k] !== defaults[k] && filters[k] !== '');
    if (activeKeys.length === 0) return null;
    return (
      <div className="active-filters-row">
        {activeKeys.filter(k => !k.includes('StartDate') && !k.includes('EndDate')).map(k => (
          <div className="active-filter-chip" key={k}>
            {k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: <span>{filters[k] === 'Custom' ? `${filters[k.replace('Preset', 'StartDate')]} to ${filters[k.replace('Preset', 'EndDate')]}` : filters[k]}</span>
            <button className="remove-filter-btn" onClick={() => setFilters({ ...filters, [k]: defaults[k] || '', ...(k.includes('Preset') ? { [k.replace('Preset', 'StartDate')]: defaults[k.replace('Preset', 'StartDate')] || '', [k.replace('Preset', 'EndDate')]: defaults[k.replace('Preset', 'EndDate')] || '' } : {}) })}>&times;</button>
          </div>
        ))}
      </div>
    );
  };

  const checkDateRange = (dateStr, startStr, endStr) => {
    if (!startStr && !endStr) return true; // Filter is not active
    if (!dateStr) return false; // Filter active but data missing
    if (startStr && dateStr < startStr) return false;
    if (endStr && dateStr > endStr) return false;
    return true;
  };

  const baseOpsFiltered = useMemo(() => {
    return allOrders.filter(o => {
      if (!['confirmed', 'ready_for_pickup', 'out_for_delivery'].includes(o.status?.toLowerCase())) return false;
      const dDate = getDeliveryDate(o);
      if (!checkDateRange(dDate, opsFilters.deliveryStartDate, opsFilters.deliveryEndDate)) return false;
      if (opsFilters.deliveryType && o.order_type !== opsFilters.deliveryType) return false;
      if (opsFilters.timeslot && o.slot_start_time !== opsFilters.timeslot) return false;
      return true;
    });
  }, [allOrders, opsFilters]);

  const displayOrders = useMemo(() => {
    if (searchQuery.length >= 4) {
      const q = searchQuery.toLowerCase();
      return allOrders.filter(o => ((o.customer_details?.name || o.customer_name || '').toLowerCase().includes(q) || (o.customer_details?.phone_number || o.customer_phone || '').includes(q)));
    }

    if (activeTab === 'DRAFT') {
      return allOrders.filter(o => {
        if (!['draft', 'pending', 'processing'].includes(o.status?.toLowerCase())) return false;
        const dDate = getDeliveryDate(o);
        if (!checkDateRange(dDate, draftFilters.deliveryStartDate, draftFilters.deliveryEndDate)) return false;
        if (draftFilters.paymentStatus && o.payment_status !== draftFilters.paymentStatus) return false;
        if (draftFilters.approvalStatus && o.approval_status !== draftFilters.approvalStatus) return false;
        if (draftFilters.deliveryType && o.order_type !== draftFilters.deliveryType) return false;
        if (draftFilters.timeslot && o.slot_start_time !== draftFilters.timeslot) return false;
        return true;
      });
    }

    if (activeTab === 'OPERATIONS') {
      if (opsSubTab === 'ALL') return baseOpsFiltered;
      if (opsSubTab === 'CONFIRMED') return baseOpsFiltered.filter(o => o.status?.toLowerCase() === 'confirmed');
      if (opsSubTab === 'READY') return baseOpsFiltered.filter(o => o.status?.toLowerCase() === 'ready_for_pickup');
      if (opsSubTab === 'OUT_FOR_DELIVERY') return baseOpsFiltered.filter(o => o.status?.toLowerCase() === 'out_for_delivery');
    }

    if (activeTab === 'ARCHIVE') {
      return allOrders.filter(o => {
        if (!['cancelled', 'delivered'].includes(o.status?.toLowerCase())) return false;
        const dDate = getDeliveryDate(o);
        if (!checkDateRange(dDate, archiveFilters.deliveryStartDate, archiveFilters.deliveryEndDate)) return false;
        if (archiveFilters.status && o.status?.toLowerCase() !== archiveFilters.status) return false;
        if (archiveFilters.deliveryType && o.order_type !== archiveFilters.deliveryType) return false;
        if (archiveFilters.timeslot && o.slot_start_time !== archiveFilters.timeslot) return false;
        return true;
      });
    }

    return [];
  }, [allOrders, searchQuery, activeTab, opsSubTab, draftFilters, opsFilters, archiveFilters, baseOpsFiltered]);

  const segmentedOperations = useMemo(() => {
    if (activeTab !== 'OPERATIONS' && searchQuery.length < 4) return null;
    const groups = {};
    displayOrders.forEach(o => {
      let key = getDeliveryDate(o) || 'No Delivery Date';
      if (key === todayStr) key = 'Today';
      else if (key === getLocalDateString(new Date(todayIST.getTime() + 24 * 60 * 60 * 1000))) key = 'Tomorrow';
      if (!groups[key]) groups[key] = [];
      groups[key].push(o);
    });
    return groups;
  }, [displayOrders, activeTab, searchQuery, todayStr, todayIST]);

  const renderMaxObjectDetails = (obj) => {
    if (!obj) return <div className="detail-kv"><span className="detail-v">Not Available</span></div>;
    const entries = Object.entries(obj).filter(([k, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return <div className="detail-kv"><span className="detail-v">Not Available</span></div>;
    return entries.map(([k, v]) => (
      <div className="detail-kv" key={k}>
        <span className="detail-k">{k.replace(/_/g, ' ')}</span>
        <span className="detail-v">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
      </div>
    ));
  };

  const DatePresetSelector = ({ label, filters, setFilters, fieldPrefix }) => (
    <div className="filter-group">
      <span className="filter-label">{label} Preset</span>
      <select className="lux-select" value={filters[`${fieldPrefix}Preset`]} onChange={e => applyDateRangePreset(e.target.value, fieldPrefix, filters, setFilters)}>
        <option value="All">All Dates</option>
        <option value="Today">Today</option>
        <option value="Yesterday">Yesterday</option>
        <option value="Tomorrow">Tomorrow</option>
        <option value="This Week">This Week</option>
        <option value="Next Week">Next Week</option>
        <option value="This Month">This Month</option>
        <option value="Custom">Custom Range</option>
      </select>
      {filters[`${fieldPrefix}Preset`] === 'Custom' && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <input type="date" className="lux-input" style={{ flex: 1 }} value={filters[`${fieldPrefix}StartDate`]} onChange={e => setFilters({ ...filters, [`${fieldPrefix}StartDate`]: e.target.value })} />
          <input type="date" className="lux-input" style={{ flex: 1 }} value={filters[`${fieldPrefix}EndDate`]} onChange={e => setFilters({ ...filters, [`${fieldPrefix}EndDate`]: e.target.value })} />
        </div>
      )}
    </div>
  );

  return (
    <div className="orders-page-wrapper">
      <div className="lux-header">
        <div className="lux-header-left">
          <div className="lux-title-outlet-group">
            <h1 className="lux-title">Orders Dashboard</h1>
            <OutletBadge businessName={businessName || businessInfo?.business_name} businessId={business_id} pageTitle="Orders" />
          </div>
          <p className="lux-subtitle">Manage draft, active operations, and archives seamlessly.</p>
        </div>
        <div className="header-controls">
          <div className="global-date-picker" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', borderRadius: '16px', padding: '0px 5px 0px 5px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'center' }}>
              <select style={{ margin: "0px" }} value={dateFilterType} onChange={e => handleDateFilterTypeChange(e.target.value)}>
                <option style={{ margin: "0px" }} value="delivery_date">Delivery Date</option>
                <option style={{ margin: "0px" }} value="order_date">Order Date</option>
              </select>
              <select style={{ margin: "0px" }} value={globalOrderDatePreset} onChange={e => applyGlobalDatePreset(e.target.value)}>
                <option value="Active Orders">Active Orders</option>
                <option value="All">All Time</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="Tomorrow">Tomorrow</option>
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="This Month">This Month</option>
                <option value="Custom">Custom Range</option>
              </select>
            </div>
            {(globalOrderDatePreset === 'Custom' || globalOrderDatePreset === 'Active Orders') && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center', width: '100%', borderTop: '1px solid var(--border-light)', paddingTop: '0.4rem', marginTop: '0.1rem', marginBottom: "0.4rem" }}>
                <input type="date" value={apiStartDate} onChange={e => setApiStartDate(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: '600', fontSize: '0.8rem', color: 'var(--text-main)' }} />
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>to</span>
                <input type="date" value={apiEndDate} onChange={e => setApiEndDate(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: '600', fontSize: '0.8rem', color: 'var(--text-main)' }} />
              </div>
            )}
          </div>
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input type="text" style={{ margin: "0px" }} className="search-input" placeholder=".       Search name or phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>
      </div>

      {searchQuery.length < 4 && (
        <>
          <div className="lux-tabs-container">
            <div className="lux-tabs">
              <button className={`lux-tab ${activeTab === 'DRAFT' ? 'active' : ''}`} onClick={() => { setActiveTab('DRAFT'); setShowFilters(false); }}>
                Drafts <span className="tab-count">{allOrders.filter(o => ['draft', 'pending', 'processing'].includes(o.status?.toLowerCase())).length}</span>
              </button>
              <button className={`lux-tab ${activeTab === 'OPERATIONS' ? 'active' : ''}`} onClick={() => { setActiveTab('OPERATIONS'); setShowFilters(false); }}>
                Operations <span className="tab-count">{allOrders.filter(o => ['confirmed', 'ready_for_pickup', 'out_for_delivery'].includes(o.status?.toLowerCase()) && getDeliveryDate(o) === todayStr).length}</span>
              </button>
              <button className={`lux-tab ${activeTab === 'ARCHIVE' ? 'active' : ''}`} onClick={() => { setActiveTab('ARCHIVE'); setShowFilters(false); }}>
                Archive <span className="tab-count">{allOrders.filter(o => ['cancelled', 'delivered'].includes(o.status?.toLowerCase())).length}</span>
              </button>
            </div>
          </div>

          <div className="filter-controls">
            {activeTab === 'OPERATIONS' && (
              <div className="sub-tabs-container">
                <div className="sub-tabs">
                  <button className={`sub-tab ${opsSubTab === 'ALL' ? 'active' : ''}`} onClick={() => setOpsSubTab('ALL')}>All <span className="sub-tab-count">{baseOpsFiltered.length}</span></button>
                  <button className={`sub-tab ${opsSubTab === 'CONFIRMED' ? 'active' : ''}`} onClick={() => setOpsSubTab('CONFIRMED')}>Confirmed <span className="sub-tab-count">{baseOpsFiltered.filter(o => o.status?.toLowerCase() === 'confirmed').length}</span></button>
                  <button className={`sub-tab ${opsSubTab === 'READY' ? 'active' : ''}`} onClick={() => setOpsSubTab('READY')}>Ready <span className="sub-tab-count">{baseOpsFiltered.filter(o => o.status?.toLowerCase() === 'ready_for_pickup').length}</span></button>
                  <button className={`sub-tab ${opsSubTab === 'OUT_FOR_DELIVERY' ? 'active' : ''}`} onClick={() => setOpsSubTab('OUT_FOR_DELIVERY')}>Out For Delivery <span className="sub-tab-count">{baseOpsFiltered.filter(o => o.status?.toLowerCase() === 'out_for_delivery').length}</span></button>
                </div>
              </div>
            )}

            <button className={`filter-icon-btn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></svg>
            </button>

            {activeTab === 'DRAFT' && renderActiveFilters(draftFilters, defaultDraft, setDraftFilters)}
            {activeTab === 'OPERATIONS' && renderActiveFilters(opsFilters, defaultOps, setOpsFilters)}
            {activeTab === 'ARCHIVE' && renderActiveFilters(archiveFilters, defaultArchive, setArchiveFilters)}
          </div>

          {showFilters && (
            <div className="hidden-filters-panel">
              <div className="hidden-filters-grid">
                {activeTab === 'DRAFT' && (
                  <>
                    <DatePresetSelector label="Delivery Date" fieldPrefix="delivery" filters={draftFilters} setFilters={setDraftFilters} />
                    <div className="filter-group"><span className="filter-label">Payment Status</span><select className="lux-select" value={draftFilters.paymentStatus} onChange={e => setDraftFilters({ ...draftFilters, paymentStatus: e.target.value })}><option value="">All</option><option value="PAID">Paid</option><option value="PENDING">Pending</option></select></div>
                    <div className="filter-group"><span className="filter-label">Approval Status</span><select className="lux-select" value={draftFilters.approvalStatus} onChange={e => setDraftFilters({ ...draftFilters, approvalStatus: e.target.value })}><option value="">All</option><option value="APPROVED">Approved</option><option value="PENDING_APPROVAL">Pending</option></select></div>
                    <div className="filter-group"><span className="filter-label">Delivery Type</span><select className="lux-select" value={draftFilters.deliveryType} onChange={e => setDraftFilters({ ...draftFilters, deliveryType: e.target.value })}><option value="">All</option><option value="D">Dine-in</option><option value="P">Pickup</option><option value="H">Home Delivery</option></select></div>
                    <div className="filter-group"><span className="filter-label">Slot Start Time</span><input type="time" className="lux-input" value={draftFilters.timeslot} onChange={e => setDraftFilters({ ...draftFilters, timeslot: e.target.value })} /></div>
                  </>
                )}
                {activeTab === 'OPERATIONS' && (
                  <>
                    <DatePresetSelector label="Delivery Date" fieldPrefix="delivery" filters={opsFilters} setFilters={setOpsFilters} />
                    <div className="filter-group"><span className="filter-label">Delivery Type</span><select className="lux-select" value={opsFilters.deliveryType} onChange={e => setOpsFilters({ ...opsFilters, deliveryType: e.target.value })}><option value="">All</option><option value="D">Dine-in</option><option value="P">Pickup</option><option value="H">Home Delivery</option></select></div>
                    <div className="filter-group"><span className="filter-label">Slot Start Time</span><input type="time" className="lux-input" value={opsFilters.timeslot} onChange={e => setOpsFilters({ ...opsFilters, timeslot: e.target.value })} /></div>
                  </>
                )}
                {activeTab === 'ARCHIVE' && (
                  <>
                    <div className="filter-group"><span className="filter-label">Status</span><select className="lux-select" value={archiveFilters.status} onChange={e => setArchiveFilters({ ...archiveFilters, status: e.target.value })}><option value="">All</option><option value="cancelled">Cancelled</option><option value="delivered">Delivered</option></select></div>
                    <DatePresetSelector label="Delivery Date" fieldPrefix="delivery" filters={archiveFilters} setFilters={setArchiveFilters} />
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {loading ? <div className="empty-state"><h3>Loading Orders...</h3></div> :
        displayOrders.length === 0 ? <div className="empty-state"><h3>No orders found.</h3><p>{searchQuery.length >= 4 ? `No match for "${searchQuery}"` : 'Try adjusting your filters.'}</p></div> :

          (activeTab === 'OPERATIONS' || searchQuery.length >= 4) ? (
            <>
              {Object.keys(segmentedOperations).sort().map(dateStr => (
                <div key={dateStr}>
                  <div className="upcoming-date-header"><span className="date-badge">📅 {dateStr} ({segmentedOperations[dateStr].length} {segmentedOperations[dateStr].length === 1 ? 'order' : 'orders'})</span></div>
                  <div className="lux-grid">
                    {segmentedOperations[dateStr].map(order => (
                      <OrderCard key={order.id} order={order} drivers={drivers} onOpenModal={setSelectedOrder} onAction={handleAction} onOpenWhatsAppChat={(ph) => { setSelectedPhone(ph); setShowWhatsAppChat(true); }} onOpenAssignModal={() => { setOrderToAssign(order); setSelectedDriverId(''); setShowAssignModal(true); }} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="lux-grid" style={{ marginTop: '1.5rem' }}>
              {displayOrders.map(order => (
                <OrderCard key={order.id} order={order} drivers={drivers} onOpenModal={setSelectedOrder} onAction={handleAction} onOpenWhatsAppChat={(ph) => { setSelectedPhone(ph); setShowWhatsAppChat(true); }} onOpenAssignModal={() => { setOrderToAssign(order); setSelectedDriverId(''); setShowAssignModal(true); }} />
              ))}
            </div>
          )
      }

      {/* Classy Detailed Order Modal */}
      {selectedOrder && (
        <div className="lux-modal-overlay" onClick={() => { setSelectedOrder(null); setIsEditing(false); }}>
          <div className="lux-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order #{selectedOrder.id}</h2>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                {!isEditing && (
                  <button className="di-cart-fab" onClick={() => startEditing(selectedOrder)}>
                    ✏️ Edit Order
                  </button>
                )}
                <button className="modal-close" onClick={() => { setSelectedOrder(null); setIsEditing(false); }}>&times;</button>
              </div>
            </div>

            {isEditing ? (
              <div className="modal-body">
                <div className="modal-section">
                  <h3 className="section-title">✏️ Edit Order Details</h3>
                  <div className="edit-form-grid">
                    <div className="edit-form-field">
                      <label className="edit-form-label">Customer Name</label>
                      <input className="lux-input" value={editForm.customer_name} onChange={e => setEditForm({ ...editForm, customer_name: e.target.value })} />
                    </div>

                    <div className="edit-form-field">
                      <label className="edit-form-label">Phone Number</label>
                      <input className="lux-input" value={editForm.customer_phone} onChange={e => setEditForm({ ...editForm, customer_phone: e.target.value })} />
                    </div>

                    <div className="edit-form-field">
                      <label className="edit-form-label">Order Type</label>
                      <select className="lux-select" value={editForm.order_type} onChange={e => handleEditOrderTypeChange(e.target.value)}>
                        <option value="HOME_DELIVERY">Home Delivery</option>
                        <option value="PICKUP">Pickup</option>
                        <option value="DINE_IN">Dine-In</option>
                      </select>
                    </div>

                    {editForm.order_type === 'HOME_DELIVERY' && (
                      <>
                        <div className="edit-form-field-full">
                          <label className="edit-form-label">Address Line 1</label>
                          <input className="lux-input" value={editForm.address1} onChange={e => { const val = e.target.value; setEditForm(prev => ({ ...prev, address1: val })); setEditDeliveryCache(c => ({ ...c, HOME_DELIVERY: { ...c.HOME_DELIVERY, address1: val } })); }} placeholder="Address Line 1" />
                        </div>
                        <div className="edit-form-field-full">
                          <label className="edit-form-label">Address Line 2</label>
                          <input className="lux-input" value={editForm.address2} onChange={e => { const val = e.target.value; setEditForm(prev => ({ ...prev, address2: val })); setEditDeliveryCache(c => ({ ...c, HOME_DELIVERY: { ...c.HOME_DELIVERY, address2: val } })); }} placeholder="Address Line 2 (Optional)" />
                        </div>
                        <div className="edit-form-field">
                          <label className="edit-form-label">City</label>
                          <input className="lux-input" value={editForm.city} onChange={e => { const val = e.target.value; setEditForm(prev => ({ ...prev, city: val })); setEditDeliveryCache(c => ({ ...c, HOME_DELIVERY: { ...c.HOME_DELIVERY, city: val } })); }} placeholder="City" />
                        </div>
                        <div className="edit-form-field">
                          <label className="edit-form-label">State / Province</label>
                          <input className="lux-input" value={editForm.province} onChange={e => { const val = e.target.value; setEditForm(prev => ({ ...prev, province: val })); setEditDeliveryCache(c => ({ ...c, HOME_DELIVERY: { ...c.HOME_DELIVERY, province: val } })); }} placeholder="State" />
                        </div>
                        <div className="edit-form-field">
                          <label className="edit-form-label">Pin Code / Zip</label>
                          <input className="lux-input" value={editForm.zip} onChange={e => handleEditPincodeChange(e.target.value)} placeholder="Pin Code" />
                        </div>
                        <div className="edit-form-field">
                          <label className="edit-form-label">Country</label>
                          <input className="lux-input" value={editForm.country} onChange={e => { const val = e.target.value; setEditForm(prev => ({ ...prev, country: val })); setEditDeliveryCache(c => ({ ...c, HOME_DELIVERY: { ...c.HOME_DELIVERY, country: val } })); }} placeholder="Country" />
                        </div>
                      </>
                    )}

                    {editForm.order_type === 'PICKUP' && (() => {
                      const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
                      const locations = pickup.locations || [];
                      return locations.length > 0 && (
                        <div className="edit-form-field">
                          <label className="edit-form-label">Pickup Location</label>
                          <select className="lux-select" value={editForm.pickupLocationId} onChange={e => handleEditPickupLocationChange(e.target.value)}>
                            <option value="">Select Location</option>
                            {locations.map(l => <option key={l.id} value={l.id}>{l.label} - {l.address}</option>)}
                          </select>
                        </div>
                      );
                    })()}

                    {editForm.order_type === 'HOME_DELIVERY' && editForm.zip && (() => {
                      const supported = getSupportedProviders(businessInfo, editForm.zip);
                      if (supported.length === 0) {
                        return (
                          <div className="edit-form-field-full" style={{ color: 'red', fontWeight: 'bold' }}>
                            No delivery providers available for this Pin Code.
                          </div>
                        );
                      }
                      if (supported.length > 1) {
                        return (
                          <div className="edit-form-field">
                            <label className="edit-form-label">Delivery Provider</label>
                            <select className="lux-select" value={editForm.deliveryProviderId} onChange={e => handleEditProviderChange(e.target.value)}>
                              <option value="">Select Provider</option>
                              {supported.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {editForm.order_type === 'PICKUP' && editForm.pickupLocationId && (() => {
                      const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
                      const loc = pickup.locations?.find(l => l.id === editForm.pickupLocationId);
                      return loc && (
                        <div className="edit-form-field-full" style={{ padding: '10px', background: '#F1F5F9', borderRadius: '6px', fontSize: '0.9rem', color: '#475569', border: '1px solid #E2E8F0', marginBottom: '1rem' }}>
                          <strong>Store Address:</strong> {loc.address || 'Pickup from store'}
                        </div>
                      );
                    })()}

                    {((editForm.order_type === 'HOME_DELIVERY' && editForm.zip && editForm.deliveryProviderId) ||
                      (editForm.order_type === 'PICKUP' && editForm.pickupLocationId) ||
                      (editForm.order_type === 'DINE_IN')) && (
                        <div className="edit-form-field-full">
                          <label className="edit-form-label">Delivery Date</label>
                          <div className="date-chips-grid">
                            {getNextNDays(7).map((d) => (
                              <button
                                key={d.value}
                                type="button"
                                className={`chip-select ${editForm.delivery_date === d.value ? 'active' : ''}`}
                                onClick={() => {
                                  const val = d.value;
                                  setEditForm(prev => ({ ...prev, delivery_date: val }));
                                  setEditDeliveryCache(c => {
                                    const key = editForm.order_type === 'HOME_DELIVERY' ? 'HOME_DELIVERY' : 'PICKUP';
                                    return { ...c, [key]: { ...c[key], delivery_date: val } };
                                  });
                                }}
                              >
                                {d.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                    {((editForm.order_type === 'HOME_DELIVERY' && editForm.zip && editForm.deliveryProviderId && editForm.delivery_date) ||
                      (editForm.order_type === 'PICKUP' && editForm.pickupLocationId && editForm.delivery_date) ||
                      (editForm.order_type === 'DINE_IN' && editForm.delivery_date)) && (
                        <div className="edit-form-field-full">
                          <label className="edit-form-label">Delivery Slot</label>
                          <div className="slot-chips-grid">
                            {(() => {
                              const getEditSlotsForSelection = () => {
                                if (editForm.order_type === 'HOME_DELIVERY') {
                                  if (!editForm.deliveryProviderId) return [];
                                  const schedulingConfig = businessInfo?.business_metadata?.order_rules?.scheduling_config || businessInfo?.business_metadata?.scheduling_config;
                                  const providers = schedulingConfig?.methods?.local_delivery?.providers || [];
                                  const provider = providers.find(p => p.id === editForm.deliveryProviderId);
                                  const slots = getProviderSlots(provider);
                                  return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
                                } else if (editForm.order_type === 'PICKUP') {
                                  if (!editForm.pickupLocationId) return [];
                                  const slots = getPickupSlots(businessInfo, editForm.pickupLocationId);
                                  return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
                                }
                                return CHECKOUT_DELIVERY_SLOTS;
                              };
                              return getEditSlotsForSelection().map((s) => {
                                const isActiveSlot = isSlotActiveForDate(s, editForm.delivery_date);
                                return (
                                  <button
                                    key={s.value}
                                    type="button"
                                    className={`chip-select ${(editForm.slot_start_time + '-' + editForm.slot_end_time) === s.value ? 'active' : ''}`}
                                    onClick={() => {
                                      const [start, end] = s.value.split('-');
                                      const slotId = s.id || '';
                                      setEditForm(prev => ({ ...prev, slot_start_time: start, slot_end_time: end, delivery_slot_id: slotId }));
                                      setEditDeliveryCache(c => {
                                        const key = editForm.order_type === 'HOME_DELIVERY' ? 'HOME_DELIVERY' : 'PICKUP';
                                        return { ...c, [key]: { ...c[key], slot_start_time: start, slot_end_time: end, delivery_slot_id: slotId } };
                                      });
                                    }}
                                    disabled={!isActiveSlot}
                                  >
                                    {s.label}
                                  </button>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}

                    {editForm.order_type === 'HOME_DELIVERY' && (
                      <div className="edit-form-field">
                        <label className="edit-form-label">Delivery Charges (₹)</label>
                        <input type="number" className="lux-input" value={editForm.delivery_charges} onChange={e => {
                          const val = parseFloat(e.target.value) || 0;
                          setEditForm(prev => ({ ...prev, delivery_charges: val }));
                          setEditDeliveryCache(c => ({ ...c, HOME_DELIVERY: { ...c.HOME_DELIVERY, delivery_charges: val } }));
                        }} />
                      </div>
                    )}

                    {editForm.order_type === 'DINE_IN' && (
                      <div className="edit-form-field">
                        <label className="edit-form-label">Table Number</label>
                        <input className="lux-input" value={editForm.table_no} onChange={e => setEditForm({ ...editForm, table_no: e.target.value })} placeholder="Table Number" />
                      </div>
                    )}

                    <div className="edit-form-field-full">
                      <label className="edit-form-label">Remark / Notes</label>
                      <textarea className="lux-input" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} placeholder="Order remarks" />
                    </div>
                  </div>
                </div>

                <div className="modal-section" style={{ marginTop: '1.5rem' }}>
                  <h3 className="section-title">🍔 Order Items</h3>
                  <div style={{ background: '#F8FAFC', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    {editForm.product_items.length === 0 ? (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>No items in order</p>
                    ) : (
                      <ul className="items-list-preview">
                        {editForm.product_items.map((item, idx) => (
                          <li key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span className="qty-badge">{item.quantity}x</span>
                              <div className="item-text-group">
                                <span className="item-name-preview">{item.name || item.product_name}</span>
                                {item.variant_name && <span className="item-variant-preview"> ({item.variant_name})</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="di-counter">
                                <button type="button" onClick={() => handleEditCartAction('decrement', idx)}>−</button>
                                <span>{item.quantity}</span>
                                <button type="button" onClick={() => handleEditCartAction('increment', idx)}>+</button>
                              </div>
                              <span style={{ fontWeight: '600', minWidth: '60px', textAlign: 'right' }}>
                                {formatCurrency(item.item_price * item.quantity, item.currency)}
                              </span>
                              <button type="button" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' }} onClick={() => handleEditCartAction('remove', idx)}>×</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="custom-item-section">
                      <div className="custom-item-section-title">➕ Add Custom Item</div>
                      <div className="custom-item-inputs">
                        <div className="edit-form-field">
                          <label className="edit-form-label">Item Name</label>
                          <input className="lux-input" value={editCustomName} onChange={e => setEditCustomName(e.target.value)} placeholder="e.g. Garlic Bread" />
                        </div>
                        <div className="edit-form-field">
                          <label className="edit-form-label">Price (₹)</label>
                          <input type="number" className="lux-input" value={editCustomPrice} onChange={e => setEditCustomPrice(e.target.value)} placeholder="150" />
                        </div>
                        <div className="edit-form-field">
                          <label className="edit-form-label">Qty</label>
                          <input type="number" className="lux-input" value={editCustomQty} onChange={e => setEditCustomQty(parseInt(e.target.value, 10) || 1)} />
                        </div>
                        <button type="button" className="btn-action btn-approve" style={{ height: '38px', padding: '0 16px' }} onClick={addEditCustomItem}>+ Add</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                  <button className="btn-action btn-reject" style={{ width: '100%', padding: '0.8rem' }} onClick={() => setIsEditing(false)}>Cancel</button>
                  <button className="btn-action btn-approve" style={{ width: '100%', padding: '0.8rem' }} onClick={() => saveEditedOrder(selectedOrder.id)}>Save Changes</button>
                </div>
              </div>
            ) : (
              <div className="modal-body">
                <div className="modal-section">
                  <h3 className="section-title">📦 Order Data</h3>
                  <div className="section-grid">
                    {renderMaxObjectDetails({
                      Status: selectedOrder.status,
                      Type: (() => {
                        const rawType = selectedOrder.order_type?.toUpperCase();
                        if (rawType === 'P' || rawType === 'PARCEL' || rawType === 'PICKUP') return 'Pickup';
                        if (rawType === 'D' || rawType === 'DINEIN' || rawType === 'DINE_IN') return 'Dine-in';
                        if (rawType === 'H' || rawType === 'HOME_DELIVERY') return 'Home Delivery';
                        return selectedOrder.order_type;
                      })(),
                      Approval_Status: selectedOrder.approval_status,
                      Source: selectedOrder.source,
                      Order_DateTime: formatDate(selectedOrder.order_datetime, true),
                      Delivery_Date: formatDate(selectedOrder.delivery_date, false),
                      Delivery_Slot: selectedOrder.delivery_slot_label || formatSlotTime(selectedOrder.slot_start_time, selectedOrder.slot_end_time) || "N/A",
                      Created_At: formatDate(selectedOrder.created_at, true)
                    })}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                  <div className="modal-section">
                    <h3 className="section-title">👤 Customer Info</h3>
                    <div className="section-grid" style={{ gridTemplateColumns: '1fr' }}>
                      {renderMaxObjectDetails(selectedOrder.customer_details || { Phone: selectedOrder.customer_phone, Name: selectedOrder.customer_name })}
                    </div>
                  </div>

                  <div className="modal-section">
                    <h3 className="section-title">📍 Full Address</h3>
                    <div className="section-grid" style={{ gridTemplateColumns: '1fr', marginBottom: '1rem' }}>
                      {renderMaxObjectDetails(selectedOrder.address)}
                    </div>
                    {(() => {
                      const loc = getLatLon(selectedOrder.address);
                      if (!loc) {
                        return <button className="btn-loc request" onClick={() => handleAction(selectedOrder, 'request_location')}>📍 Request Location via WhatsApp</button>;
                      } else {
                        return <button className="btn-loc directions" onClick={() => window.open(`https://www.google.com/maps?q=${loc.lat},${loc.lon}`, '_blank')}>🧭 Open in Google Maps</button>;
                      }
                    })()}
                  </div>
                </div>

                {selectedOrder.description && (
                  <div className="modal-section">
                    <h3 className="section-title">📝 Remark / Notes</h3>
                    <p style={{ background: '#F8FAFC', padding: '0.8rem', borderRadius: '6px', border: '1px solid var(--border-light)', margin: 0, fontSize: '0.92rem' }}>
                      {selectedOrder.description}
                    </p>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                  <div className="modal-section">
                    <h3 className="section-title">💳 Financial Details</h3>
                    <div className="section-grid" style={{ gridTemplateColumns: '1fr' }}>
                      {renderMaxObjectDetails({
                        Payment_Status: selectedOrder.payment_status,
                        Currency: selectedOrder.currency,
                        Subtotal: selectedOrder.total_amount,
                        Delivery_Charges: selectedOrder.delivery_charges,
                        Tax_Total: selectedOrder.tax_total,
                        Discount: selectedOrder.discount_total,
                        ...selectedOrder.payment_details
                      })}
                    </div>
                  </div>

                  <div className="modal-section">
                    <h3 className="section-title">🍔 Order Items</h3>
                    <div style={{ background: '#F8FAFC', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                      <OrderItemsList items={selectedOrder.product_items?.product_items} detailed={true} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showRejectModal && orderToReject && (() => {
        const isPaid = orderToReject.payment_status === 'PAID';
        const maxRefundAmount = orderToReject.payment_details?.amount?.value
          ? orderToReject.payment_details.amount.value / 100
          : (orderToReject.total_amount || 0);

        const currentAmtNum = parseFloat(rejectAmount);
        const isExceeded = isPaid && !isNaN(currentAmtNum) && currentAmtNum > maxRefundAmount;
        const isNegative = !isNaN(currentAmtNum) && currentAmtNum < 0;
        const isSubmitDisabled = !rejectReason.trim() || isExceeded || isNegative;

        return (
          <div className="lux-modal-overlay" onClick={() => setShowRejectModal(false)}>
            <div className="lux-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
              <div className="lux-modal-header">
                <h2>Reject Order #{orderToReject.id}</h2>
                <button className="close-btn" onClick={() => setShowRejectModal(false)}>&times;</button>
              </div>
              <div className="lux-modal-content">
                <div className="filter-group" style={{ marginBottom: '1.2rem', display: 'flex', flexDirection: 'column' }}>
                  <span className="filter-label" style={{ fontWeight: '700', marginBottom: '4px' }}>
                    Reason for Rejection <span style={{ color: '#DC2626' }}>*</span>
                  </span>
                  <input
                    type="text"
                    className="lux-input"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="e.g. Items out of stock, Restaurant closed"
                    required
                  />
                </div>

                {isPaid ? (
                  <div className="filter-group" style={{ marginBottom: '1.2rem', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span className="filter-label" style={{ fontWeight: '700' }}>
                        Refund Amount (Max ₹{maxRefundAmount})
                      </span>
                      <button
                        type="button"
                        className="btn-full-refund"
                        onClick={() => setRejectAmount(maxRefundAmount.toString())}
                      >
                        Full Refund (₹{maxRefundAmount})
                      </button>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={maxRefundAmount}
                      className="lux-input"
                      value={rejectAmount}
                      onChange={e => setRejectAmount(e.target.value)}
                      placeholder={`Enter amount up to ₹${maxRefundAmount}`}
                    />
                    <div className="refund-helper-bar">
                      <span className="max-amount-hint">Payment status: PAID (Max ₹{maxRefundAmount})</span>
                    </div>
                    {isExceeded && (
                      <div className="refund-warning-text">
                        ⚠️ Refund amount cannot exceed order total (₹{maxRefundAmount})
                      </div>
                    )}
                    {isNegative && (
                      <div className="refund-warning-text">
                        ⚠️ Refund amount cannot be negative
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="refund-info-box">
                    💳 <strong>Payment Status:</strong> {orderToReject.payment_status || 'UNPAID'} — No refund will be initiated since payment has not been received.
                  </div>
                )}

                <div className="filter-group" style={{ marginBottom: '1.2rem', display: 'flex', flexDirection: 'column' }}>
                  <span className="filter-label" style={{ fontWeight: '700', marginBottom: '4px' }}>Refund Speed</span>
                  <select
                    className="lux-select"
                    value={rejectSpeed}
                    onChange={e => setRejectSpeed(e.target.value)}
                  >
                    <option value="normal">Normal (Standard Processing)</option>
                    <option value="instant">Instant (Immediate Refund)</option>
                  </select>
                </div>
              </div>
              <div className="lux-modal-footer">
                <button
                  className="btn-action btn-reject"
                  style={{ width: '100%', padding: '0.8rem', opacity: isSubmitDisabled ? 0.6 : 1 }}
                  onClick={confirmRejectOrder}
                  disabled={isSubmitDisabled}
                >
                  Confirm Reject {isPaid && rejectAmount ? `& Refund ₹${rejectAmount}` : ''}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showAssignModal && orderToAssign && (
        <div className="lux-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="lux-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header"><h2>Assign Driver & Dispatch</h2><button className="modal-close" onClick={() => setShowAssignModal(false)}>&times;</button></div>
            <div className="modal-body">
              <p style={{ marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Optionally select a driver before marking this order as picked up / out for delivery.</p>
              <select className="lux-select" style={{ width: '100%' }} value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)}>
                <option value="">No Driver Assigned</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name} ({d.phone_number})</option>)}
              </select>
              <div style={{ marginTop: '1.5rem', display: 'flex' }}>
                <button className="btn-action btn-ready" style={{ padding: '1rem', fontSize: '1rem', width: '100%' }} onClick={confirmDispatch}>Confirm Dispatch</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMarkReadyModal && (
        <div className="lux-modal-overlay" onClick={() => setShowMarkReadyModal(false)}>
          <div className="lux-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header"><h2>Upload Order Photo</h2><button className="modal-close" onClick={() => setShowMarkReadyModal(false)}>&times;</button></div>
            <div className="modal-body">
              <p style={{ marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Please attach a photo of the prepared order before marking it as ready.</p>
              <MediaUpload type="IMAGE" onUpload={handleMediaUpload} onRemove={() => { setMediaId(null); setMediaPreviewUrl(''); }} previewUrl={mediaPreviewUrl} uploading={uploadingMedia} isUploaded={!!mediaId} />
              <div style={{ marginTop: '1.5rem', display: 'flex' }}>
                <button className="btn-action btn-ready" style={{ padding: '1rem', fontSize: '1rem', width: '100%' }} disabled={!mediaId || uploadingMedia} onClick={submitMarkReadyWithPhoto}>Confirm Mark Ready</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWhatsAppChat && <WhatsAppChatModal phone_number={selectedPhone} onClose={() => setShowWhatsAppChat(false)} token={token} phone_number_id={whatsappPhoneNumberId} />}

      {showCreateOrderModal && (
        <div className="lux-modal-overlay" onClick={closeCreateOrderFlow}>
          <div className="lux-modal create-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Order</h2>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <button className="di-cart-fab" onClick={() => setCreateCartOpen(true)} style={{ opacity: createCartQty === 0 ? 0.8 : 1 }}>
                  Cart {createCartQty > 0 ? `(${createCartQty})` : ''}
                </button>
                <button className="modal-close" onClick={closeCreateOrderFlow}>&times;</button>
              </div>
            </div>
            <div className="modal-body create-order-body">
              {menuLoading && <div className="empty-state"><h3>Loading menu...</h3></div>}
              {menuError && <div className="empty-state"><h3>{menuError}</h3></div>}
              {!menuLoading && !menuError && (
                <>
                  <nav className="di-category-bar" aria-label="Menu categories">
                    {activeCreateCategories.map((cat) => (
                      <button
                        key={cat.categoryid}
                        className={`di-cat-tab ${activeCreateCat === cat.categoryid ? 'active' : ''}`}
                        onClick={() => setActiveCreateCat(cat.categoryid)}
                      >
                        {cat.categoryname}
                      </button>
                    ))}
                  </nav>
                  <div className="create-order-items">
                    {itemsForCreateCategory(activeCreateCat).map((item) => (
                      <div key={item.itemid} className="di-item-card">
                        <div className="di-item-thumb">{item.item_image_url ? <img src={item.item_image_url} alt={item.itemname} /> : '🍽️'}</div>
                        <div className="di-item-body">
                          <div className="di-item-name">{item.itemname}</div>
                          <div className="di-item-desc">{item.itemdescription}</div>
                          <div className="di-item-footer">
                            <div className="di-item-price">
                              <span className="di-currency">₹</span>
                              {String(item.itemallowvariation) === '1'
                                ? (() => {
                                  const prices = (item.variation || []).filter((v) => isActive(v.active)).map((v) => parseFloat(v.price || 0));
                                  if (prices.length === 0) return '0';
                                  const min = Math.min(...prices);
                                  const max = Math.max(...prices);
                                  return min === max ? min.toFixed(0) : `${min.toFixed(0)}-${max.toFixed(0)}`;
                                })()
                                : parseFloat(item.price || 0).toFixed(0)}
                            </div>
                            <button className="di-add-btn" onClick={() => setCreateItemDrawer(item)}>+ Add</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="custom-item-section" style={{ marginTop: '1rem', background: '#F8FAFC', width: 'auto', margin: '1rem' }}>
                    <div className="custom-item-section-title">➕ Add Custom Item (Not in Menu)</div>
                    <div className="custom-item-inputs">
                      <div className="edit-form-field">
                        <label className="edit-form-label" style={{ fontSize: '0.7rem' }}>Item Name</label>
                        <input className="lux-input" style={{ padding: '6px' }} value={createCustomName} onChange={e => setCreateCustomName(e.target.value)} placeholder="e.g. Garlic Bread" />
                      </div>
                      <div className="edit-form-field">
                        <label className="edit-form-label" style={{ fontSize: '0.7rem' }}>Price (₹)</label>
                        <input type="number" className="lux-input" style={{ padding: '6px' }} value={createCustomPrice} onChange={e => setCreateCustomPrice(e.target.value)} placeholder="150" />
                      </div>
                      <div className="edit-form-field">
                        <label className="edit-form-label" style={{ fontSize: '0.7rem' }}>Qty</label>
                        <input type="number" className="lux-input" style={{ padding: '6px' }} value={createCustomQty} onChange={e => setCreateCustomQty(parseInt(e.target.value, 10) || 1)} />
                      </div>
                      <button type="button" className="btn-action btn-approve" style={{ padding: '8px 12px', height: '36px' }} onClick={addCreateCustomItem}>+ Add Custom</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {createItemDrawer && (
        <CreateOrderItemDrawer
          item={createItemDrawer}
          addongroups={createAddongroups}
          onClose={() => setCreateItemDrawer(null)}
          onAddToCart={addCreateCartItem}
        />
      )}

      {createCartOpen && (
        <CreateOrderCartDrawer
          cart={createCart}
          onClose={() => setCreateCartOpen(false)}
          onCartAction={onCreateCartAction}
          onCheckout={() => { setCreateCartOpen(false); setCreateCheckoutOpen(true); }}
        />
      )}

      {createCheckoutOpen && (() => {
        const getSlotsForSelection = () => {
          if (checkoutForm.orderType === 'HOME_DELIVERY') {
            if (!checkoutForm.deliveryProviderId) return [];
            const schedulingConfig = businessInfo?.business_metadata?.order_rules?.scheduling_config || businessInfo?.business_metadata?.scheduling_config;
            const providers = schedulingConfig?.methods?.local_delivery?.providers || [];
            const provider = providers.find(p => p.id === checkoutForm.deliveryProviderId);
            const slots = getProviderSlots(provider);
            return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
          } else if (checkoutForm.orderType === 'PICKUP') {
            if (!checkoutForm.pickupLocationId) return [];
            const slots = getPickupSlots(businessInfo, checkoutForm.pickupLocationId);
            return slots.length > 0 ? slots : CHECKOUT_DELIVERY_SLOTS;
          }
          return [];
        };

        return (
          <div className="lux-modal-overlay" onClick={() => setCreateCheckoutOpen(false)}>
            <div className="lux-modal checkout-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Checkout</h2>
                <button className="modal-close" onClick={() => setCreateCheckoutOpen(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="filter-group"><span className="filter-label">Customer Name</span><input className="lux-input" value={checkoutForm.name} onChange={(e) => setCheckoutForm((prev) => ({ ...prev, name: e.target.value }))} /></div>
                <div className="filter-group"><span className="filter-label">Phone Number</span><input className="lux-input" value={checkoutForm.phone_number} onChange={(e) => setCheckoutForm((prev) => ({ ...prev, phone_number: e.target.value }))} placeholder="+919876543210" /></div>

                <div className="filter-group">
                  <span className="filter-label">Delivery Type</span>
                  <select className="lux-select" value={checkoutForm.orderType} onChange={(e) => handleCheckoutOrderTypeChange(e.target.value)}>
                    <option value="">Select Delivery Type</option>
                    <option value="HOME_DELIVERY">Home Delivery</option>
                    <option value="PICKUP">Pickup</option>
                  </select>
                </div>

                {checkoutForm.orderType === 'HOME_DELIVERY' && (
                  <>
                    <div className="filter-group">
                      <span className="filter-label">Address</span>
                      <textarea className="lux-input" value={checkoutForm.address} onChange={(e) => setCheckoutForm((prev) => ({ ...prev, address: e.target.value }))} placeholder="Enter delivery address" />
                    </div>
                    <div className="filter-group">
                      <span className="filter-label">Pin Code</span>
                      <input className="lux-input" value={checkoutForm.pincode} onChange={(e) => handleCheckoutPincodeChange(e.target.value)} placeholder="Pin Code" />
                    </div>
                  </>
                )}

                {checkoutForm.orderType === 'PICKUP' && (() => {
                  const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
                  const locations = pickup.locations || [];
                  return locations.length > 0 && (
                    <div className="filter-group">
                      <span className="filter-label">Pickup Location</span>
                      <select className="lux-select" value={checkoutForm.pickupLocationId} onChange={e => handleCheckoutPickupLocationChange(e.target.value)}>
                        <option value="">Select Location</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.label} - {l.address}</option>)}
                      </select>
                    </div>
                  );
                })()}

                {checkoutForm.orderType === 'HOME_DELIVERY' && checkoutForm.pincode && (() => {
                  const supported = getSupportedProviders(businessInfo, checkoutForm.pincode);
                  if (supported.length === 0) {
                    return (
                      <div className="filter-group" style={{ color: 'red', fontWeight: 'bold' }}>
                        No delivery providers available for this Pin Code.
                      </div>
                    );
                  }
                  if (supported.length > 1) {
                    return (
                      <div className="filter-group">
                        <span className="filter-label">Delivery Provider</span>
                        <select className="lux-select" value={checkoutForm.deliveryProviderId} onChange={e => handleCheckoutProviderChange(e.target.value)}>
                          <option value="">Select Provider</option>
                          {supported.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                      </div>
                    );
                  }
                  return null;
                })()}

                {checkoutForm.orderType === 'PICKUP' && checkoutForm.pickupLocationId && (() => {
                  const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
                  const loc = pickup.locations?.find(l => l.id === checkoutForm.pickupLocationId);
                  return loc && (
                    <div className="filter-group" style={{ padding: '10px', background: '#F1F5F9', borderRadius: '6px', fontSize: '0.9rem', color: '#475569', border: '1px solid #E2E8F0' }}>
                      <strong>Store Address:</strong> {loc.address || 'Pickup from store'}
                    </div>
                  );
                })()}

                {((checkoutForm.orderType === 'HOME_DELIVERY' && checkoutForm.pincode && checkoutForm.deliveryProviderId) ||
                  (checkoutForm.orderType === 'PICKUP' && checkoutForm.pickupLocationId)) && (
                    <div className="filter-group">
                      <span className="filter-label">Delivery Date</span>
                      <div className="date-chips-grid">
                        {getNextNDays(7).map((d) => (
                          <button
                            key={d.value}
                            type="button"
                            className={`chip-select ${checkoutForm.delivery_date === d.value ? 'active' : ''}`}
                            onClick={() => setCheckoutForm((prev) => ({ ...prev, delivery_date: d.value }))}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                {((checkoutForm.orderType === 'HOME_DELIVERY' && checkoutForm.pincode && checkoutForm.deliveryProviderId && checkoutForm.delivery_date) ||
                  (checkoutForm.orderType === 'PICKUP' && checkoutForm.pickupLocationId && checkoutForm.delivery_date)) && (
                    <div className="filter-group">
                      <span className="filter-label">Delivery Slot</span>
                      <div className="slot-chips-grid">
                        {getSlotsForSelection().map((s) => {
                          const isActiveSlot = isSlotActiveForDate(s, checkoutForm.delivery_date);
                          return (
                            <button
                              key={s.value}
                              type="button"
                              className={`chip-select ${checkoutForm.delivery_slot === s.value ? 'active' : ''}`}
                              onClick={() => setCheckoutForm((prev) => ({ ...prev, delivery_slot: s.value, delivery_slot_id: s.id || '' }))}
                              disabled={!isActiveSlot}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {checkoutForm.delivery_slot && (
                  <>
                    {checkoutForm.orderType === 'HOME_DELIVERY' && (
                      <div className="filter-group">
                        <span className="filter-label">Delivery/Shipping Charges (₹)</span>
                        <input type="number" className="lux-input" value={checkoutForm.deliveryCharges} onChange={e => setCheckoutForm(prev => ({ ...prev, deliveryCharges: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 40" />
                      </div>
                    )}

                    <div className="filter-group">
                      <span className="filter-label">Remark / Notes</span>
                      <textarea className="lux-input" value={checkoutForm.description} onChange={(e) => setCheckoutForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Enter order remarks" />
                    </div>
                  </>
                )}

                <button className="btn-action btn-ready" style={{ width: '100%', marginTop: '1rem' }} onClick={createOrderCheckout} disabled={creatingOrder || !checkoutForm.delivery_slot}>
                  {creatingOrder ? 'Creating order...' : `Create Order (${formatCurrency(createCartTotal + (parseFloat(checkoutForm.deliveryCharges) || 0), 'INR')})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <button className="create-order-btn create-order-btn-floating" onClick={() => navigate('/orders/create')} aria-label="Create order">+</button>
    </div>
  );
}
